import moment from 'moment';
import { addDays, subDays, isBefore, isAfter } from 'date-fns';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  onSnapshot,
  deleteField,
} from 'firebase/firestore';
import { ref, uploadBytes, deleteObject } from 'firebase/storage';
import { orderBy } from 'lodash';

import {
  LOAD_CASES,
  SET_CASES,
  LOAD_CASE_STATS,
  SET_CASE_STATS,
  SET_ACTIVE_TAB,
  SET_SELECTED_FLOW,
  CREATE_CASE,
  UPDATE_CASE,
  SET_ACTIVE_CASE,
  DISCARD_ACTIVE_CASE,
  RESET_CASES,
  LOAD_CASE_NOTES,
  SET_CASE_NOTES,
  ADD_CASE_NOTE,
  UPDATE_CASE_NOTE,
  SET_COMPLETED_CASES,
  LOAD_COMPLETED_CASES,
  SET_ARCHIVED_CASES,
} from './actionTypes';
import Firebase from '../firebase';
import {
  CASES_COLLECTION,
  SURGEONS_COLLECTION,
  NOTES_SUB_COLLECTION,
  TEAMS_COLLECTION,
  MANUFACTURERS_COLLECTION,
  LOGISTICS_COLLECTION,
} from '../firebase/collections';

import { fetchProcedures } from './proceduresActions';
import { createActivity } from './activityActions';
import { setNotification } from './notificationActions';
import { getDistributors } from './surgeonsActions';

import { api, sendEmail } from '../util';

import { caseConstants, userRoles, emailTemplates } from '../constants';
import { companyRoles, countryRoles, distributorRoles, roleNames } from '../constants/userRoles';
import urls from '../constants/urls';
import { casesACLEmails, casesCMFEmails, casesCMFExtendedEmails } from '../constants/emailTemplates';
// import { DEFAULT_COMPANY_ID } from '../constants/cases';

const {
  stepRestrictions,
  cmfExtendedStepRestrictions,
  aclStepRestrictions,
  restrictionsInTrainingMode,
  caseNotifications,
  caseACLNotifications,
  caseCMFNotifications,
  caseCMFExtendedNotifications,
  alertMessages,
  alertAclMessages,
  alertCmfMessages,
  alertCmfExtendedMessages,
  procedureFlows,
  cmfExtendedProcedureFlows,
  matorthoProcedureFlows,
  numberOfSteps,
  fileStatuses,
  fileTypes,
  statuses,
  DEFAULT_COMPANY_ID,
} = caseConstants;
const { casesEmails } = emailTemplates;

const internalCaseNoteRoles = [
  roleNames.GLOBAL_ADMIN,
  roleNames.DISTRIBUTOR_ADMIN,
  roleNames.ENGINEER,
];

const canAccessInternalCaseNotes = (role) => internalCaseNoteRoles.includes(role);

const toFirestoreDateString = (value) => {
  if (!value && value !== 0) {
    return value;
  }

  const date = moment.isMoment(value) ? value : moment(value);

  return date.isValid() ? date.format() : value;
};

const normalizeNestedDateFields = (data, keys) => {
  if (!data || typeof data !== 'object') {
    return;
  }

  keys.forEach((key) => {
    if (data[key]) {
      data[key] = toFirestoreDateString(data[key]);
    }
  });
};

const normalizeTopLevelDateField = (data, key) => {
  if (Object.prototype.hasOwnProperty.call(data, key)) {
    data[key] = toFirestoreDateString(data[key]);
  }
};

const getFirestoreCaseData = (caseData = {}, options = {}) => {
  const docData = { ...caseData };
  const { deleteHydrationField = false } = options;

  delete docData.access;
  delete docData.sortDate;
  delete docData.caseNotes;
  delete docData.isHydrated;
  delete docData.companyName;
  delete docData.companyCountryKey;
  delete docData.distributor;
  delete docData.hospitalId;
  delete docData.procedureId;
  delete docData.requestedSurgeryDateCurrent;
  delete docData.requestedSurgeryDateNew;
  delete docData.requestedSurgeryDateUserId;
  delete docData.requestedSurgeryDateUserName;
  delete docData.requestedSurgeryDateCurrentHospital;
  delete docData.requestedSurgeryDateNewHospital;
  delete docData.requestedConsultationDateCurrent;
  delete docData.requestedConsultationDateNew;
  delete docData.requestedConsultationDateUserId;
  delete docData.requestedConsultationDateUserName;

  normalizeTopLevelDateField(docData, 'date');
  normalizeTopLevelDateField(docData, 'createdAt');
  normalizeTopLevelDateField(docData, 'updatedAt');
  normalizeTopLevelDateField(docData, 'consultationDate');

  normalizeNestedDateFields(docData.requestedSurgeryDate, ['currentDate', 'newDate']);
  normalizeNestedDateFields(docData.requestedConsultationDate, ['currentDate', 'newDate']);

  if (caseData.procedureId) {
    docData.procedure = caseData.procedureId;
  }

  if (caseData.surgeonId && typeof docData.surgeon === 'string' && !docData.surgeon.startsWith('/')) {
    docData.surgeon = `/${SURGEONS_COLLECTION}/${caseData.surgeonId}`;
  }

  if (deleteHydrationField) {
    docData.isHydrated = deleteField();
  }

  return docData;
};

const getCaseStoragePath = (caseId, fileId) => `${caseId}/${fileId}`;

const deleteCaseStorageFile = async (storage, path) => {
  try {
    await deleteObject(ref(storage, path));
  } catch (error) {
    if (error.code !== 'storage/object-not-found') {
      throw error;
    }
  }
};

const replaceCaseStorageFile = async (storage, oldPath, newPath, file, updateMetadata) => {
  await uploadBytes(ref(storage, newPath), file);
  await updateMetadata();

  if (oldPath && oldPath !== newPath) {
    await deleteCaseStorageFile(storage, oldPath);
  }
};

const isRejectedCaseFile = (file) => file?.status === fileStatuses.REJECTED;

const getUploadedFileRecords = (caseFiles = [], uploadedFiles = []) => (
  uploadedFiles
    .map((uploadedFile) => {
      const matchingFiles = caseFiles.filter((file) => file.name === uploadedFile.name);

      return matchingFiles.find((file) => !isRejectedCaseFile(file)) || matchingFiles[0];
    })
);

const getReplacementTypesFromCaseFiles = (files = []) => {
  const rejectedTypes = new Set(
    files
      .filter(isRejectedCaseFile)
      .map((file) => file.type)
      .filter(Boolean)
  );

  return new Set(
    files
      .filter((file) => !isRejectedCaseFile(file) && rejectedTypes.has(file.type))
      .map((file) => file.type)
      .filter(Boolean)
  );
};

const getReplacementTypesFromUploadedFiles = (caseFiles = [], uploadedFiles = []) => new Set(
  getUploadedFileRecords(caseFiles, uploadedFiles)
    .map((file) => file?.type)
    .filter(Boolean)
);

const removeReplacedRejectedFiles = (files = [], replacementTypes = new Set()) => {
  if (!replacementTypes.size) {
    return { files, rejectedFiles: [] };
  }

  const rejectedFiles = files.filter((file) => (
    isRejectedCaseFile(file) && replacementTypes.has(file.type)
  ));

  if (!rejectedFiles.length) {
    return { files, rejectedFiles: [] };
  }

  return {
    files: files.filter((file) => (
      !isRejectedCaseFile(file) || !replacementTypes.has(file.type)
    )),
    rejectedFiles,
  };
};

const deleteReplacedRejectedFileStorage = async (storage, caseId, rejectedFiles = [], retainedFiles = []) => {
  const retainedFileIds = new Set(retainedFiles.map((file) => file.id).filter(Boolean));

  await Promise.all(
    rejectedFiles
      .filter((file) => file.id && !retainedFileIds.has(file.id))
      .map((file) => deleteCaseStorageFile(storage, getCaseStoragePath(caseId, file.id)))
  );
};

const isUnhydratedCase = (caseData = {}) => caseData.isHydrated === false;

const canWriteCaseFiles = (caseData = {}, actionName) => {
  if (isUnhydratedCase(caseData)) {
    console.error(`Refusing to update files for unhydrated case in ${actionName}:`, caseData.id);
    return false;
  }

  return true;
};

const hasAccess = (currentUser, surgeon, step, flow, trainingMode = false) => {
  const {
    GLOBAL_ADMIN,
    COUNTRY_ADMIN,
    RADIOLOGY,
    SALES_REP,
    PRACTICE_MANAGER,
    REGISTRAR,
  } = roleNames;

  if (currentUser.role === RADIOLOGY && step === 1) {
    return true;
  }

  const isCmfExtendedFlow = cmfExtendedProcedureFlows.includes(flow);

  const restrictions = isCmfExtendedFlow
    ? cmfExtendedStepRestrictions
    : flow === procedureFlows.ACL
      ? aclStepRestrictions
      : stepRestrictions;

  const isStepOutOfBounds = step > numberOfSteps;

  const hasEngineerLevel1Override = currentUser?.engineerLevel === 1;

  const isAdmin =
    currentUser.role === GLOBAL_ADMIN ||
    currentUser.role === COUNTRY_ADMIN;

  const rolesRequiringSurgeonCheck = [
    SALES_REP,
    PRACTICE_MANAGER,
    REGISTRAR,
  ];

  const requiresSurgeonCheck = rolesRequiringSurgeonCheck.includes(
    currentUser.role
  );

  const isAssignedToSurgeon =
    surgeon &&
    Object.values(surgeon).some(
      (value) =>
        (Array.isArray(value) && value.includes(currentUser.uid)) ||
        value === currentUser.uid
    );

  const hasSurgeonAccess =
    !requiresSurgeonCheck || isAssignedToSurgeon || isAdmin;

  const hasStepAccess = (list) =>
    list?.[step]?.includes(currentUser.role);

  const hasBaseAccess = trainingMode
    ? hasStepAccess(restrictionsInTrainingMode) || isStepOutOfBounds
    : hasStepAccess(restrictions) ||
    isStepOutOfBounds ||
    hasEngineerLevel1Override ||
    (hasStepAccess(restrictions) && step === 1);

  const isAccess = trainingMode
    ? hasBaseAccess
    : hasBaseAccess && hasSurgeonAccess;

  if (currentUser?.engineerLevel === 3) {
    return (
      (isStepOutOfBounds || step === 0 || step === 1) &&
      Object.values(surgeon || {}).some(
        (value) => value && value.includes(currentUser.uid)
      )
    );
  }

  return isAccess;
};

export const setCases = (cases) => ({ type: SET_CASES, cases });
export const setCaseStats = (stats) => ({ type: SET_CASE_STATS, stats });
export const setCompletedCases = (cases) => ({ type: SET_COMPLETED_CASES, cases });
export const setArchivedCases = (cases) => ({ type: SET_ARCHIVED_CASES, cases });

export const setActiveTab = (tab) => ({ type: SET_ACTIVE_TAB, tab });
export const setSelectedFlow = (flow) => ({ type: SET_SELECTED_FLOW, flow });

export const getTeams = () => async () => {
  const db = Firebase.db;

  const teamsCollection = collection(db, TEAMS_COLLECTION);
  const q = query(teamsCollection);

  const snapshot = await getDocs(q);

  let teams = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  return teams;
};

export const getManufacturers = () => async () => {
  const db = Firebase.db;

  const manufacturersCollection = collection(db, MANUFACTURERS_COLLECTION);
  const q = query(manufacturersCollection);

  const snapshot = await getDocs(q);

  let manufacturers = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  return manufacturers;
};

export const getLogistics = () => async () => {
  const db = Firebase.db;

  const logisticsCollection = collection(db, LOGISTICS_COLLECTION);
  const q = query(logisticsCollection);

  const snapshot = await getDocs(q);

  let logistics = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  return logistics;
};

const getAssignedSurgeonIdsFromList = (surgeons = [], currentUser = {}) => {
  if (!currentUser?.uid) {
    return [];
  }

  if (currentUser.role === userRoles.SURGEON.name) {
    return surgeons
      .filter((surgeon) => surgeon.userId === currentUser.uid)
      .map((surgeon) => surgeon.id);
  }

  const roleFieldMap = {
    [userRoles.SALES_REP.name]: userRoles.SALES_REP.field,
    [userRoles.PRACTICE_MANAGER.name]: userRoles.PRACTICE_MANAGER.field,
    [userRoles.REGISTRAR.name]: userRoles.REGISTRAR.field,
  };

  const roleField = roleFieldMap[currentUser.role];

  if (!roleField) {
    return [];
  }

  return surgeons
    .filter((surgeon) => {
      const assignedUsers = surgeon?.[roleField];

      if (Array.isArray(assignedUsers)) {
        return assignedUsers.includes(currentUser.uid);
      }

      return assignedUsers === currentUser.uid;
    })
    .map((surgeon) => surgeon.id);
};

export const fetchCases = (queryParams) => async (dispatch, getState) => {
  try {
    const state = getState();
    const currentUser = state.user.currentUser;
    // const companies = state.companies?.list || [];

    let distributors = state.distributors?.list || [];
    let teams = state.teams?.list || [];
    let manufacturers = state.manufacturers?.list || [];
    let logistics = state.logistics?.list || [];
    let surgeons = state.surgeons?.list || [];

    const params = { ...queryParams };

    if (companyRoles.includes(currentUser?.role)) {
      params.company = currentUser?.company || null;
    }

    if (countryRoles.includes(currentUser?.role)) {
      params.countries = currentUser?.administrationCountries || [];
    }

    if (distributorRoles.includes(currentUser?.role)) {
      if (!distributors.length) {
        distributors = await dispatch(getDistributors());
      }

      params.distributorIds = currentUser?.distributors?.length ? currentUser?.distributors : ['empty'];
    }

    if (currentUser?.role === userRoles.ENGINEER.name) {
      if (!teams.length) {
        teams = await dispatch(getTeams());
      }
      const engineeringTeams = teams.filter((t) => t.users?.includes(currentUser.uid));
      const procedures = [];
      engineeringTeams.forEach((team) => {
        team.procedures?.forEach((procedureId) => {
          if (!procedures.includes(procedureId)) {
            procedures.push(procedureId);
          }
        });
      });

      params.procedureIds = procedures.length ? procedures : ['empty'];

      if (engineeringTeams.some((team) => !!team.allProcedures)) {
        delete params.procedureIds;
      }
    }

    if (
      [
        userRoles.SURGEON.name,
        userRoles.SALES_REP.name,
        userRoles.PRACTICE_MANAGER.name,
        userRoles.REGISTRAR.name,
      ].includes(currentUser?.role)
    ) {
      let assignedSurgeonIds = getAssignedSurgeonIdsFromList(surgeons, currentUser);

      if (!assignedSurgeonIds.length) {
        const assignedSurgeons = await fetchSurgeons(currentUser.uid, currentUser.role);
        assignedSurgeonIds = assignedSurgeons?.map((surgeon) => surgeon.id) || [];
      }

      params.surgeonIds = assignedSurgeonIds.length ? assignedSurgeonIds : ['empty'];

      if ([userRoles.SALES_REP.name, userRoles.PRACTICE_MANAGER.name, userRoles.REGISTRAR.name].includes(currentUser?.role)) {
        delete params.distributorIds;
      }
    }

    if (currentUser?.role === userRoles.MANUFACTURER.name) {
      // Option 1 – Fetch cases by manufacturer team id
      if (!manufacturers.length) {
        manufacturers = await dispatch(getManufacturers());
      }

      const manufacturerTeams = manufacturers?.filter((m) => m.users?.includes(currentUser.uid));
      params.manufacturerIds = manufacturerTeams?.map((t) => t.id);

      // Option 2 – Fetch cases by unique pair of company and country keys
      // const keys = [];
      // companies?.forEach((company) => {
      //   company?.countryConfig?.forEach((config) => {
      //     if (manufacturerTeams?.map((t) => t.id).includes(config.manufacturer)) {
      //       keys.push(`${company.id}_${config.country}`);
      //     }
      //   });
      // });
      // params.companyCountryKeys = keys;
    }

    if (currentUser?.role === userRoles.LOGISTICS.name) {
      if (!logistics.length) {
        logistics = await dispatch(getLogistics());
      }

      const logisticsTeams = logistics?.filter((l) => l.users?.includes(currentUser.uid));
      params.logisticsIds = logisticsTeams?.map((t) => t.id);
    }

    const res = await api.post(urls.getCases, { params });

    return res ? res.body : null;
  } catch (err) {
    throw err;
  }
};

export const fetchCaseStats = () => async (dispatch, getState) => {
  try {
    const state = getState();
    const trainingMode = state.sidebar.trainingMode;

    dispatch({ type: LOAD_CASE_STATS });

    const [totalRes, futureRes, postponedRes] = await Promise.all([
      dispatch(fetchCases({ page: 1, perPage: 1, trainingMode })),
      dispatch(fetchCases({ page: 1, perPage: 1, statuses: [], dateFrom: moment().valueOf(), trainingMode })),
      dispatch(fetchCases({ page: 1, perPage: 1, statuses: ['Postponed'], trainingMode })),
    ]);

    dispatch(setCaseStats({
      totalCases: totalRes?.total || 0,
      futureCases: futureRes?.total || 0,
      postponedCases: postponedRes?.total || 0,
    }));
  } catch (err) {
    console.error('Error fetching case stats:', err);
    dispatch(setCaseStats({
      totalCases: 0,
      futureCases: 0,
      postponedCases: 0,
    }));
  }
};

const isFutureCaseDate = (date) => {
  if (!date) {
    return false;
  }

  const timestamp = moment(date).valueOf();
  return !Number.isNaN(timestamp) && timestamp > moment().valueOf();
};

const scheduleCaseStatsRefresh = (dispatch) => {
  setTimeout(() => {
    dispatch(fetchCaseStats());
  }, 2000);
};

const getCaseCompanyId = (caseData = {}, state = {}) => {
  if (caseData.company) {
    return caseData.company;
  }

  const distributor = state.distributors?.list?.find((item) => item.id === caseData.distributorId);

  return distributor?.company || state.user?.currentUser?.company || DEFAULT_COMPANY_ID;
};

const userMatchesCaseCompany = (user = {}, caseData = {}, state = {}) => {
  if (!user?.uid) {
    return false;
  }

  if (user.role === userRoles.GLOBAL_ADMIN.name) {
    return true;
  }

  if (!companyRoles.includes(user.role)) {
    return true;
  }

  return (user.company || DEFAULT_COMPANY_ID) === getCaseCompanyId(caseData, state);
};

const isCountryAdminForCase = (user = {}, caseData = {}, state = {}) => (
  user.role === userRoles.COUNTRY_ADMIN.name &&
  user.administrationCountries?.includes(caseData.country) &&
  userMatchesCaseCompany(user, caseData, state)
);

const isDistributorAdminForCase = (user = {}, caseData = {}, state = {}) => (
  user.role === userRoles.DISTRIBUTOR_ADMIN.name &&
  user.distributors?.includes(caseData.distributorId) &&
  userMatchesCaseCompany(user, caseData, state)
);

const withCaseActivityCompany = (caseData = {}, activity = {}, state = {}) => ({
  ...activity,
  company: getCaseCompanyId(caseData, state),
});

const getCasePatientLastName = (caseData = {}) => (
  caseData.patientInfo?.lastName || caseData.patientLastName || ''
);

const getCaseSurgeon = (caseData = {}, surgeons = []) => {
  if (caseData.surgeon && typeof caseData.surgeon === 'object' && caseData.surgeon.userId) {
    return caseData.surgeon;
  }

  const surgeonId = caseData.surgeonId || caseData.surgeon?.id;

  if (surgeonId) {
    return surgeons.find((s) => s.id === surgeonId);
  }

  return surgeons.find((s) => caseData.surgeon === `/${SURGEONS_COLLECTION}/${s.id}`);
};

const getSurgeonName = (caseData = {}, surgeons = [], users = []) => {
  const surgeon = getCaseSurgeon(caseData, surgeons);
  const surgeonUser = users.find((u) => u.uid === surgeon?.userId);

  if (surgeonUser) {
    return surgeonUser.fullName || [surgeonUser.firstName, surgeonUser.lastName].filter(Boolean).join(' ');
  }

  return caseData.surgeonName || '';
};

const updateCaseStatsOptimistically = (dispatch, getState, updater) => {
  const currentStats = getState().cases?.stats || {
    totalCases: 0,
    futureCases: 0,
    postponedCases: 0,
  };

  dispatch(setCaseStats(updater(currentStats)));
};

const uniqByUid = (recipients = []) => {
  const seen = new Set();

  return recipients.filter((recipient) => {
    if (!recipient?.uid || seen.has(recipient.uid)) {
      return false;
    }

    seen.add(recipient.uid);
    return true;
  });
};

const teamMatchesCase = (team = {}, caseData = {}) => {
  const procedureId = caseData?.procedure || caseData?.procedureId;
  const teamProcedures = team?.procedures || [];
  const teamCountries = team?.countries || [];

  const matchesProcedure = !!team?.allProcedures || (procedureId && teamProcedures.includes(procedureId));
  const matchesCountry = !teamCountries.length || !caseData?.country || teamCountries.includes(caseData.country);

  return matchesProcedure && matchesCountry;
};

const getEngineerRecipientsFromTeams = (caseData = {}, users = [], teams = []) => {
  const engineerIds = new Set();

  teams
    .filter((team) => teamMatchesCase(team, caseData))
    .forEach((team) => {
      team.users?.forEach((uid) => engineerIds.add(uid));
    });

  return users.filter((user) => (
    user?.role === userRoles.ENGINEER.name &&
    engineerIds.has(user.uid)
  ));
};

const getTeamRecipientsById = (teamId, users = [], teams = [], roleName) => {
  const team = teams.find((item) => item.id === teamId);
  const memberIds = new Set(team?.users || []);

  return users.filter((user) => (
    user?.role === roleName &&
    memberIds.has(user.uid)
  ));
};

const getRoleRecipients = (
  role,
  surgeon = {},
  caseData = {},
  users = [],
  teams = [],
  manufacturers = [],
  logistics = [],
) => {
  if (role?.name === roleNames.ENGINEER) {
    return getEngineerRecipientsFromTeams(caseData, users, teams);
  }

  if (role?.name === roleNames.MANUFACTURER) {
    return getTeamRecipientsById(caseData?.manufacturerId, users, manufacturers, userRoles.MANUFACTURER.name);
  }

  if (role?.name === roleNames.LOGISTICS) {
    return getTeamRecipientsById(caseData?.logisticsId, users, logistics, userRoles.LOGISTICS.name);
  }

  const recipientId = role?.name === roleNames.SURGEON ? surgeon?.userId : surgeon?.[role?.field];

  if (!recipientId) {
    return [];
  }

  const recipientIds = Array.isArray(recipientId) ? recipientId : [recipientId];

  return uniqByUid(recipientIds.map((id) => users.find((user) => user.uid === id)))
    .filter((user) => userMatchesCaseCompany(user, caseData));
};

const getRecipientsForRoles = (roles = [], surgeon = {}, caseData = {}, users = [], teams = [], manufacturers = [], logistics = []) => (
  uniqByUid(roles.flatMap((role) => getRoleRecipients(role, surgeon, caseData, users, teams, manufacturers, logistics)))
);

export const fetchCasesByDate = (fromDate, toDate) => async () => {
  const db = Firebase.db;

  try {
    const casesQuery = query(
      collection(db, CASES_COLLECTION),
      where("date", ">=", fromDate.toISOString()),
      where("date", "<=", toDate.toISOString())
    );

    const snapshot = await getDocs(casesQuery);
    const cases = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    return cases;
  } catch (err) {
    throw err;
  }
};

// export const fetchCases = (withoutLoading) => async (dispatch, getState) => {
//   const db = Firebase.db;
//   const state = getState();
//   const uid = state.user.currentUser?.uid;
//   const role = state.user.currentUser?.role;
//   const country = state.user?.currentUser?.administrationCountry || null;
//   const users = state.users.list;
//   const distributors = state.distributors.list;
//
//   if (!withoutLoading) {
//     dispatch({ type: LOAD_CASES });
//   }
//
//   dispatch(fetchProcedures());
//
//   try {
//     const surgeons = await fetchSurgeons(uid, role);
//     const surgeonIds = surgeons.map((item) => item.id);
//
//     if (!surgeonIds.length) {
//       return dispatch(setCases([]));
//     }
//
//     const results = [];
//     const surgeonQueries = surgeonIds.map((surgeonId) =>
//       getDocs(query(collection(db, CASES_COLLECTION), where('surgeonId', '==', surgeonId))).then((snapshot) => {
//         snapshot.docs.forEach((doc) => {
//           results.push({ id: doc.id, ...doc.data() });
//         });
//       })
//     );
//
//     await Promise.all(surgeonQueries);
//
//     const filteredCases = results.filter((item) => role === userRoles.GLOBAL_ADMIN.name || !item.archived);
//
//     // Fetch the surgeon data from Firestore using document references
//     const promises = filteredCases.map(async (item) => {
//       // Convert surgeon path (if it's stored as a string) into a DocumentReference
//       const surgeonRef = typeof item.surgeon === 'string'
//         ? doc(Firebase.db, item.surgeon)
//         : item.surgeon;
//
//       // Fetch the surgeon document data
//       const surgeonDoc = await getDoc(surgeonRef);
//
//       // Return the data if the document exists
//       return surgeonDoc.exists() ? { ...surgeonDoc.data(), id: surgeonDoc?.id || '' } : null;
//     });
//
//     const surgeonsData = await Promise.all(promises);
//
//     surgeonsData.forEach((surgeon, index) => {
//       const currentCase = filteredCases[index];
//       const caseSurgeon = { id: surgeon?.id, ...surgeon };
//       const surgeonUser = users.find((user) => user.uid === caseSurgeon?.userId);
//       const surgeonName = surgeonUser ? `${surgeonUser?.firstName} ${surgeonUser?.lastName}` : '';
//       const currentUser = state.user.currentUser;
//       const isTrainingMode = currentCase.trainingMode;
//
//       currentCase.surgeon = caseSurgeon;
//       currentCase.surgeonName = surgeonName;
//       currentCase.access = hasAccess(currentUser, caseSurgeon, currentCase.step, currentCase.procedureFlow, isTrainingMode);
//     });
//
//     const finalCases = role === userRoles.COUNTRY_ADMIN.name
//       ? filteredCases.filter((item) => distributors.find((d) => d.id === item.distributorId)?.country === country)
//       : filteredCases;
//
//     dispatch(setCases(finalCases.sort((a, b) => (a.date > b.date ? 1 : -1))));
//   } catch (error) {
//     dispatch(setCases([]));
//     console.error("Error fetching cases:", error);
//   }
// };

export const fetchCase = (caseId) => async (dispatch, getState) => {
  const db = Firebase.db;
  const state = getState();

  const currentUser = state.user.currentUser;
  const users = state.users.list;

  dispatch(fetchProcedures());

  try {
    const caseRef = doc(db, CASES_COLLECTION, caseId);
    const caseSnap = await getDoc(caseRef);

    if (!caseSnap.exists()) {
      console.warn('Case not found:', caseId);
      return;
    }

    const caseData = {
      id: caseSnap.id,
      ...caseSnap.data(),
    };

    let surgeon = null;
    let surgeonName = '';

    if (caseData.surgeon) {
      const surgeonRef =
        typeof caseData.surgeon === 'string'
          ? doc(db, caseData.surgeon)
          : caseData.surgeon;

      const surgeonSnap = await getDoc(surgeonRef);

      if (surgeonSnap.exists()) {
        surgeon = {
          id: surgeonSnap.id,
          ...surgeonSnap.data(),
        };

        const surgeonUser = users.find(
          (user) => user.uid === surgeon.userId
        );

        surgeonName = surgeonUser
          ? `${surgeonUser.firstName} ${surgeonUser.lastName}`
          : '';
      }
    }

    const isTrainingMode = caseData.trainingMode;

    const access = hasAccess(
      currentUser,
      surgeon,
      caseData.step,
      caseData.procedureFlow,
      isTrainingMode
    );

    const fullCase = {
      ...caseData,
      surgeon,
      surgeonName,
      access,
    };

    dispatch(setActiveCase(fullCase));

    return fullCase;
  } catch (error) {
    console.error('Error fetching case:', error);
  }
};

export const fetchCompletedCases = () => async (dispatch, getState) => {
  const db = Firebase.db;
  const state = getState();
  const currentUser = state.user.currentUser;
  const role = currentUser?.role;
  const uid = currentUser?.uid;
  const users = state?.users?.list || [];
  const distributors = state?.distributors?.list || [];
  const surgeons = state?.surgeons?.list || [];

  // Skip fetch for Radiology role
  if (role === userRoles.RADIOLOGY.name) {
    console.log("Fetch skipped for Radiology role.");
    return;
  }

  try {
    dispatch({ type: LOAD_COMPLETED_CASES });

    const completedCasesQuery = query(
      collection(db, CASES_COLLECTION),
      where("status", "==", "COMPLETED"),
    );

    const snapshot = await getDocs(completedCasesQuery);
    let completedCases = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    console.log(`Fetched ${completedCases.length} completed cases`);

    const enrichedCases = await Promise.all(
      completedCases.map(async (caseData) => {
        const surgeonRef =
          typeof caseData.surgeon === "string"
            ? doc(Firebase.db, caseData.surgeon)
            : caseData.surgeon;
        const surgeonData = await fetchSurgeonData(surgeonRef);

        const surgeonUser = users.find(
          (user) => user.uid === surgeonData?.userId
        );
        const surgeonName = surgeonUser
          ? `${surgeonUser?.firstName} ${surgeonUser?.lastName}`
          : "";
        const currentUser = state.user.currentUser;
        const isTrainingMode = caseData.trainingMode;

        return {
          ...caseData,
          surgeon: surgeonData,
          surgeonName,
          access: hasAccess(
            currentUser,
            surgeonData,
            caseData.step,
            caseData.procedureFlow,
            isTrainingMode
          ),
        };
      })
    );

    let filteredCases = enrichedCases;
    if (role === userRoles.COUNTRY_ADMIN.name) {
      filteredCases = filteredCases.filter((item) =>
        isCountryAdminForCase(currentUser, {
          ...item,
          country: item.country || distributors.find((d) => d.id === item.distributorId)?.country,
        }, state)
      );
    } else if (role === userRoles.SURGEON.name) {
      filteredCases = filteredCases.filter(
        (caseItem) => caseItem.surgeon?.userId === uid
      );
    } else if (role !== userRoles.GLOBAL_ADMIN.name) {
      filteredCases = filteredCases.filter(
        (caseItem) => surgeons?.map((s) => s.id).includes(caseItem.surgeonId)
      );
    }

    dispatch(
      setCompletedCases(filteredCases.sort((a, b) => (a.date > b.date ? 1 : -1)))
    );
  } catch (error) {
    console.error("Error fetching completed cases:", error);
  }
};

export const fetchArchivedCases = () => async (dispatch, getState) => {
  const db = Firebase.db;
  const state = getState();
  const currentUser = state.user.currentUser;
  const role = currentUser?.role;
  const uid = currentUser?.uid;
  const users = state.users.list;
  const distributors = state.distributors.list;

  // Skip fetch for Radiology role
  if (role === userRoles.RADIOLOGY.name) {
    console.log("Fetch skipped for Radiology role.");
    return; // Early return to skip fetching completed cases
  }

  try {
    dispatch({ type: LOAD_COMPLETED_CASES });
    // Firestore query to fetch cases with 'status' === 'COMPLETED'
    const archivedCasesQuery = query(
      collection(db, CASES_COLLECTION),
      where("archived", "==", true),
    );

    // Execute the query
    const snapshot = await getDocs(archivedCasesQuery);
    let archivedCases = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    console.log(`Fetched ${archivedCases.length} deleted cases`);

    // Fetch surgeon data and enrich cases
    const enrichedCases = await Promise.all(
      archivedCases.map(async (caseData) => {
        const surgeonRef =
          typeof caseData.surgeon === "string"
            ? doc(Firebase.db, caseData.surgeon)
            : caseData.surgeon;
        const surgeonData = await fetchSurgeonData(surgeonRef);

        const surgeonUser = users.find(
          (user) => user.uid === surgeonData?.userId
        );
        const surgeonName = surgeonUser
          ? `${surgeonUser?.firstName} ${surgeonUser?.lastName}`
          : "";
        const currentUser = state.user.currentUser;
        const isTrainingMode = caseData.trainingMode;

        return {
          ...caseData,
          surgeon: surgeonData,
          surgeonName,
          access: hasAccess(
            currentUser,
            surgeonData,
            caseData.step,
            caseData.procedureFlow,
            isTrainingMode
          ),
        };
      })
    );

    // Apply role-specific filtering
    let filteredCases = enrichedCases;
    if (role === userRoles.COUNTRY_ADMIN.name) {
      filteredCases = filteredCases.filter((item) =>
        isCountryAdminForCase(currentUser, {
          ...item,
          country: item.country || distributors.find((d) => d.id === item.distributorId)?.country,
        }, state)
      );
    }

    if (role === userRoles.SURGEON.name) {
      filteredCases = filteredCases.filter(
        (caseItem) => caseItem.surgeon?.userId === uid
      );
    }

    // Dispatch sorted completed cases
    dispatch(
      setArchivedCases(filteredCases.sort((a, b) => (a.date > b.date ? 1 : -1)))
    );
  } catch (error) {
    console.error("Error fetching deleted cases:", error);
  }
};

// Fetch completed cases in chunks

// export const fetchCompletedCases = () => async (dispatch, getState) => {
//   const db = Firebase.db;
//   const state = getState();
//   const role = state.user.currentUser?.role;
//   const uid = state.user.currentUser?.uid;
//   const country = state.user?.currentUser?.administrationCountry || null;
//   const users = state.users.list;
//   const distributors = state.distributors.list;
//
//   // Skip fetch for Radiology role
//   if (role === userRoles.RADIOLOGY.name) {
//     console.log("Fetch skipped for Radiology role.");
//     return; // Early return to skip fetching completed cases
//   }
//
//   try {
//     dispatch({ type: LOAD_COMPLETED_CASES });
//
//     const chunkSize = 500; // Number of documents to fetch per chunk
//     let lastVisible = null; // For pagination
//     let isMoreData = true; // Track if more data is available
//
//     while (isMoreData) {
//       // Create Firestore query with pagination
//       const completedCasesQuery = lastVisible
//         ? query(
//           collection(db, CASES_COLLECTION),
//           where("status", "==", "COMPLETED"),
//           startAfter(lastVisible),
//           limit(chunkSize)
//         )
//         : query(
//           collection(db, CASES_COLLECTION),
//           where("status", "==", "COMPLETED"),
//           limit(chunkSize)
//         );
//
//       // Fetch a single chunk
//       const snapshot = await getDocs(completedCasesQuery);
//
//       // If no more documents are returned, stop fetching
//       if (snapshot.empty) {
//         isMoreData = false;
//         break;
//       }
//
//       // Add current chunk of cases
//       const currentChunk = snapshot.docs.map((doc) => ({
//         id: doc.id,
//         ...doc.data(),
//       }));
//
//       console.log(`Fetched ${currentChunk.length} cases in this chunk`);
//
//       // Fetch surgeon data and enrich cases
//       const enrichedCases = await Promise.all(
//         currentChunk.map(async (caseData) => {
//           const surgeonRef =
//             typeof caseData.surgeon === "string"
//               ? doc(Firebase.db, caseData.surgeon)
//               : caseData.surgeon;
//           const surgeonData = await fetchSurgeonData(surgeonRef);
//
//           const surgeonUser = users.find(
//             (user) => user.uid === surgeonData?.userId
//           );
//           const surgeonName = surgeonUser
//             ? `${surgeonUser?.firstName} ${surgeonUser?.lastName}`
//             : "";
//           const currentUser = state.user.currentUser;
//           const isTrainingMode = caseData.trainingMode;
//
//           return {
//             ...caseData,
//             surgeon: surgeonData,
//             surgeonName,
//             access: hasAccess(
//               currentUser,
//               surgeonData,
//               caseData.step,
//               caseData.procedureFlow,
//               isTrainingMode
//             ),
//           };
//         })
//       );
//
//       // Apply role-specific filtering
//       let filteredCases = currentChunk;
//       if (role === userRoles.COUNTRY_ADMIN.name) {
//         filteredCases = filteredCases.filter((item) =>
//           distributors
//             .find((d) => d.id === item.distributorId)
//             ?.country === country
//         );
//       }
//
//       if (role === userRoles.SURGEON.name) {
//         filteredCases = filteredCases.filter(
//           (caseItem) => caseItem.surgeon?.userId === uid
//         );
//       }
//
//       // Dispatch the chunk to the store
//       dispatch(setCompletedCases(filteredCases));
//
//       console.log(`Dispatched ${filteredCases.length} cases`);
//
//       // Update last visible document for pagination
//       lastVisible = snapshot.docs[snapshot.docs.length - 1];
//     }
//   } catch (error) {
//     console.error("Error fetching completed cases:", error);
//   }
// };

export const fetchCaseById = (caseId) => async (dispatch, getState) => {
  const state = getState();
  const users = state.users.list;
  const db = Firebase.db;

  try {
    const caseDocRef = doc(db, CASES_COLLECTION, caseId);
    const docSnapshot = await getDoc(caseDocRef);

    const q = query(collection(db, CASES_COLLECTION), where('formattedId', '==', caseId));
    const snapshot = await getDocs(q);
    const docs = snapshot?.docs || [];

    if (docSnapshot.exists() || docs?.length) {
      dispatch(fetchProcedures());
      const data = docSnapshot.exists() ? docSnapshot.data() : docs[0]?.data();
      const id =  docSnapshot.exists() ? docSnapshot.id : docs[0]?.id;

      const surgeonDocRef = doc(db, SURGEONS_COLLECTION, data.surgeonId);
      const surgeonSnap = await getDoc(surgeonDocRef);
      const surgeon = surgeonSnap.data();
      const caseSurgeon = users.find((user) => user.uid === surgeon?.userId);
      const surgeonName = caseSurgeon ? `${caseSurgeon?.firstName} ${caseSurgeon?.lastName}` : '';

      return {
        ...data,
        id,
        surgeon,
        surgeonName,
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error fetching case by ID:", error);
  }
};

export const fetchCasesByPatientData = (lastName, dateOfBirth) => async (dispatch, getState) => {
  const state = getState();
  const users = state.users.list;
  const db = Firebase.db;

  dispatch(fetchProcedures());

  try {
    const q = query(collection(db, CASES_COLLECTION), where('patientInfo.lastName', '==', lastName));
    const snapshot = await getDocs(q);

    const promises = snapshot.docs.map(async (docSnap) => {
      const data = docSnap.data();
      const surgeonDocRef = doc(db, SURGEONS_COLLECTION, data.surgeonId);
      const surgeonSnap = await getDoc(surgeonDocRef);
      const surgeon = surgeonSnap.data();
      const caseSurgeon = users.find((user) => user.uid === surgeon.userId);
      const surgeonName = caseSurgeon ? `${caseSurgeon.firstName} ${caseSurgeon.lastName}` : '';

      return {
        ...data,
        id: docSnap.id,
        surgeon,
        surgeonName,
      };
    });

    const cases = await Promise.all(promises);

    return cases.filter((item) => {
      const dateBefore = subDays(new Date(dateOfBirth), 1);
      const dateAfter = addDays(new Date(dateOfBirth), 1);
      const date = new Date(moment(item.patientInfo.dateOfBirth).format('MM/DD/YYYY'));

      return isBefore(dateBefore, date) && isAfter(dateAfter, date);
    });
  } catch (error) {
    console.error("Error fetching cases by patient data:", error);
  }
};

export const fetchRejectedCases = () => async (dispatch, getState) => {
  const state = getState();
  const userId = state.user.currentUser.uid;
  const users = state.users.list;
  const db = Firebase.db;

  dispatch(fetchProcedures());

  try {
    const q = query(
      collection(db, CASES_COLLECTION),
      where('uploadFilesUid', '==', userId),
      where('status', '==', 'REJECTED')
    );
    const snapshot = await getDocs(q);

    const promises = snapshot.docs.map(async (docSnap) => {
      const data = docSnap.data();
      const surgeonDocRef = doc(db, SURGEONS_COLLECTION, data.surgeonId);
      const surgeonSnap = await getDoc(surgeonDocRef);
      const surgeon = surgeonSnap.data();
      const caseSurgeon = users.find((user) => user.uid === surgeon.userId);
      const surgeonName = caseSurgeon ? `${caseSurgeon.firstName} ${caseSurgeon.lastName}` : '';

      return {
        ...data,
        id: docSnap.id,
        surgeon,
        surgeonName,
      };
    });

    return await Promise.all(promises);
  } catch (error) {
    console.error("Error fetching rejected cases:", error);
  }
};

const fetchSurgeons = async (uid, role) => {
  const db = Firebase.db;
  const { SURGEON, ENGINEER, SALES_REP, LOGISTICS, MANUFACTURER, PRACTICE_MANAGER, REGISTRAR } = roleNames;
  const userRole = userRoles[role];

  try {
    const collectionRef = collection(db, SURGEONS_COLLECTION);
    let q;

    if ([ENGINEER, SALES_REP, LOGISTICS, MANUFACTURER, PRACTICE_MANAGER, REGISTRAR].includes(role) && userRole) {
      const opStr = [ENGINEER, SALES_REP, PRACTICE_MANAGER, REGISTRAR].includes(role) ? 'array-contains' : '==';
      q = query(collectionRef, where(userRole.field, opStr, uid));
    } else if (role === SURGEON) {
      q = query(collectionRef, where('userId', '==', uid));
    } else {
      q = query(collectionRef);
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("Error fetching surgeons:", error);
  }
};

export const createCase = (uid, caseId, data, files) => async (dispatch, getState) => {
  const db = Firebase.db;
  const storage = Firebase.storage;
  const state = getState();
  const currentUser = state.user.currentUser;
  const users = state.users.list;
  const teams = state.teams.list;
  const manufacturers = state.manufacturers.list;
  const logistics = state.logistics.list;
  const surgeons = state.surgeons.list;
  const procedures = state.procedures.list;
  const companies = state.companies.list;
  const companyId = getCaseCompanyId(data, state);

  const surgeon = surgeons.find((item) => item.id === data.surgeon);
  const isMatorthoPlanOnly =
    matorthoProcedureFlows.includes(data.procedureFlow) &&
    data.skipPSI &&
    data.planningFileRequired;
  const step = data.skipPSI && !isMatorthoPlanOnly ? 10 : 1;
  const access = surgeon && hasAccess(currentUser, surgeon, step, data.procedureFlow);
  const trainingMode = state.sidebar.trainingMode;

  const surgeonUser = users.find((u) => u.uid === surgeon?.userId || u.uid === data?.surgeon);
  const procedure = procedures.find((p) => p.id === data.procedure);

  dispatch({ type: LOAD_CASES });

  try {
    const arr = caseId?.split('_');
    const formattedId = arr?.length > 1 ? arr[0] : caseId;

    const company = companies?.find((c) => c.id === companyId);
    const config = company?.countryConfig || [];
    const countryConfig = config.find((c) => c.country === data?.country) || {};
    const manufacturerId = countryConfig?.manufacturer || null;
    const logisticsId = countryConfig?.logistics || null;

    await setDoc(doc(db, CASES_COLLECTION, caseId), {
      ...getFirestoreCaseData(data),
      company: companyId,
      userId: uid,
      distributorId: data?.distributorId || null,
      manufacturerId,
      logisticsId,
      step,
      surgeon: `/${SURGEONS_COLLECTION}/${data.surgeon}`,
      surgeonId: data.surgeon,
      patientLastName: data.patientInfo.lastName,
      createdAt: moment().format('YYYY-MM-DD hh:mm A'),
      archived: false,
      postponed: false,
      formattedId,
    });

    const fileUploadPromises = files.map((file) => {
      const currentFile = data.files.find((item) => item.name === file.name);
      const type = currentFile?.type;
      if (type) {
        const path = `${caseId}/${caseId}_${type}_${file.name}`;
        return uploadBytes(ref(storage, path), file);
      }

      return null;
    });

    await Promise.all(fileUploadPromises);

    let emails = casesEmails;
    if (data.procedureFlow === procedureFlows.ACL) {
      emails = casesACLEmails;
    } else if (data.procedureFlow === procedureFlows.CMF) {
      emails = casesCMFEmails;
    } else if (cmfExtendedProcedureFlows.includes(data.procedureFlow)) {
      emails = casesCMFExtendedEmails;
    }

    const email = emails[0].success;
    const engineerRecipients = email?.roles?.find((r) => r.name === userRoles.ENGINEER.name)
      ? getEngineerRecipientsFromTeams(data, users, teams)
      : [];

    if (!trainingMode) {
      email?.roles?.forEach((role) => {
        const caseData = {
          ...data,
          company: companyId,
          manufacturerId,
          logisticsId,
        };
        const recipients = getRoleRecipients(role, surgeon, caseData, users, teams, manufacturers, logistics);

        recipients.forEach((recipient) => {
          if (recipient?.uid !== uid && !recipient?.disabled) {
            dispatch(
              createActivity(recipient.uid, withCaseActivityCompany(caseData, {
                ...caseNotifications[0].success,
                caseId,
                date: moment().format(),
                userId: currentUser?.uid || '',
              }, state))
            );

            if (role.name !== roleNames.SURGEON && !recipient?.disabled) {
              dispatch(
                sendCaseEmail(
                  recipient,
                  email.subject,
                  email.message({
                    caseId: caseId.includes("_") ? caseId.substring(0, caseId.indexOf("_")) : caseId,
                    patientLastName: data.patientLastName || '',
                  }),
                  email.additionalText,
                  true,
                  [
                    { key: 'CASE ID', value: caseId.includes("_") ? caseId.substring(0, caseId.indexOf("_")) : caseId },
                    { key: 'PATIENT SURNAME', value: data.patientInfo?.lastName || '' },
                    { key: 'SURGEON', value: surgeonUser ? `${surgeonUser.firstName} ${surgeonUser.lastName}` : '' },
                    { key: 'DOS', value: data.date ? moment(data.date).format('DD/MM/YY') : 'TBC' },
                    { key: 'HOSPITAL', value: data.hospital },
                    { key: 'PROCEDURE', value: procedure ? procedure.name : '' },
                  ]
                )
              );
            }
          }
        });
      });

      users.filter((u) =>
          isCountryAdminForCase(u, { ...data, company: companyId }, state) ||
          isDistributorAdminForCase(u, { ...data, company: companyId }, state) ||
          engineerRecipients.some((engineer) => engineer.uid === u.uid)
        )
        .forEach((u) => {
          dispatch(
            createActivity(u.uid, withCaseActivityCompany({ ...data, company: companyId }, {
              ...caseNotifications[0].success,
              caseId,
              date: moment().format(),
              userId: currentUser?.uid || '',
            }, state))
          );
        });
    }

    dispatch(setNotification({ variant: 'success', vertical: 'top', message: alertMessages[0].success }));
    updateCaseStatsOptimistically(dispatch, getState, (stats) => ({
      ...stats,
      totalCases: stats.totalCases + 1,
      futureCases: stats.futureCases + (isFutureCaseDate(data?.date) ? 1 : 0),
    }));
    scheduleCaseStatsRefresh(dispatch);

    dispatch({
      type: CREATE_CASE,
      case: {
        id: caseId,
        ...data,
        patientLastName: data.patientInfo.lastName,
        userId: uid,
        step,
        access,
        surgeon: surgeon || data.surgeon,
      },
    });
  } catch (error) {
    console.error("Error creating case:", error);
  }
};

export const clearRejectedFiles = async (filesData = [], caseId, replacementTypes = getReplacementTypesFromCaseFiles(filesData)) => {
  if (!caseId) {
    return filesData;
  }

  const storage = Firebase.storage;
  const { files, rejectedFiles } = removeReplacedRejectedFiles(filesData, replacementTypes);

  try {
    await deleteReplacedRejectedFileStorage(storage, caseId, rejectedFiles, files);
  } catch (error) {
    console.error("Error clearing replaced rejected files:", error);
  }

  return files;
};

export const updateCaseAdmin = (id, data, previousData, files) => async (dispatch, getState) => {
  const db = Firebase.db;
  const storage = Firebase.storage;
  const state = getState();
  const trainingMode = state.sidebar.trainingMode || previousData.trainingMode;
  const uid = state.user.currentUser?.uid;

  if (!trainingMode) {
    dispatch(sendChangeDateHospitalEmail(id, data, previousData));
  }

  const docData = getFirestoreCaseData(data, { deleteHydrationField: true });
  docData.company = getCaseCompanyId(docData, state);
  delete docData.surgeonData;

  if (docData.files?.length) {
    const files = [...docData.files];
    docData.files = files.map((file) => ({ ...file, downloadLink: file?.downloadLink || null }));
  }

  if (data.procedure !== previousData.procedure) {
    const newActivity = {
      userId: uid,
      date: moment().format(),
      type: 'activity',
      title: 'Procedure Change',
      text: 'Procedure has been changed',
    };
    docData.activity = [newActivity, ...docData.activity];
  }

  if (data.hospital !== previousData.hospital || (data.date && (!previousData?.date || !moment(data.date).isSame(moment(previousData?.date), 'day')))) {
    const newActivity = {
      userId: uid,
      date: moment().format(),
      type: 'activity',
      title: 'Case Updated',
      text: 'Case has been updated',
    };
    docData.activity = [newActivity, ...docData.activity];
  }

  try {
    await updateDoc(doc(db, CASES_COLLECTION, id), {
      ...docData,
      surgeon: `/${SURGEONS_COLLECTION}/${data.surgeonId}`,
    });

    const fileUploadPromises = files.map((file) => {
      const currentFile = data.files.find((item) => item.name === file.name);
      const type = currentFile?.type;

      if (type) {
        return uploadBytes(ref(storage, `${data.id}/${data.id}_${type}_${file.name}`), file);
      }

      return null;
    });

    await Promise.all(fileUploadPromises);

    dispatch({
      type: UPDATE_CASE,
      case: {
        ...data,
        surgeon: data.surgeonData || previousData.surgeon,
        isPaid: data.isPaid,
        activity: docData.activity,
      },
    });
  } catch (error) {
    console.error("Error updating case admin:", error);
  }
};

export const updateCaseEngineer = (id, data, previousData) => async (dispatch, getState) => {
  const db = Firebase.db;
  const state = getState();
  const uid = state.user.currentUser?.uid;

  const docData = getFirestoreCaseData(data, { deleteHydrationField: true });
  docData.company = getCaseCompanyId(docData, state);

  if (data.procedure !== previousData.procedure) {
    const newActivity = {
      userId: uid,
      date: moment().format(),
      type: 'activity',
      title: 'Procedure Change',
      text: 'Procedure has been changed',
    };
    docData.activity = [newActivity, ...docData.activity];
  }

  try {
    await updateDoc(doc(db, CASES_COLLECTION, id), {
      ...docData,
      surgeon: `/${SURGEONS_COLLECTION}/${data.surgeonId}`,
    });

    dispatch({ type: UPDATE_CASE, case: { ...data, surgeon: previousData.surgeon, activity: docData.activity } });
  } catch (error) {
    console.error("Error updating case admin:", error);
  }
};

export const uploadFiles = (activeCase, stepData) => async (dispatch, getState) => {
  const db = Firebase.db;
  const storage = Firebase.storage;
  const state = getState();
  const currentUser = state.user.currentUser;
  const caseData = activeCase || state.cases.activeCase;
  const users = state.users.list;
  const teams = state.teams.list;
  const surgeon = caseData.surgeon;
  const isTrainingMode = caseData.trainingMode;
  const access = hasAccess(currentUser, surgeon, 2, caseData.procedureFlow, isTrainingMode);
  const filesData = stepData.files;

  if (!canWriteCaseFiles(caseData, 'uploadFiles')) {
    return;
  }

  const caseId = caseData.id;
  const replacementTypes = getReplacementTypesFromCaseFiles(filesData);
  const { files, rejectedFiles } = removeReplacedRejectedFiles(filesData, replacementTypes);

  dispatch({ type: LOAD_CASES });

  stepData.files = files;
  if (currentUser.role === roleNames.RADIOLOGY) {
    stepData.radiologyUser = currentUser.uid;
  }

  try {
    await updateDoc(doc(db, CASES_COLLECTION, caseId), { ...stepData, step: 2, isHydrated: deleteField() });

    try {
      await deleteReplacedRejectedFileStorage(storage, caseId, rejectedFiles, files);
    } catch (error) {
      console.error("Error deleting replaced rejected files:", error);
    }

    let emails = casesEmails;
    if (caseData.procedureFlow === procedureFlows.ACL) {
      emails = casesACLEmails;
    } else if (caseData.procedureFlow === procedureFlows.CMF) {
      emails = casesCMFEmails;
    } else if (cmfExtendedProcedureFlows.includes(caseData.procedureFlow)) {
      emails = casesCMFExtendedEmails;
    }

    const email = emails[1].success;
    const recipients = getEngineerRecipientsFromTeams(caseData, users, teams);

    if (!isTrainingMode) {
      recipients.forEach((recipient) => {
        if (recipient?.uid !== currentUser?.uid && !recipient?.disabled) {
          dispatch(sendCaseEmail(
            recipient,
            email.subject,
            email.message({ caseId: caseId.includes("_") ? caseId.substring(0, caseId.indexOf("_")) : caseId }),
            email.additionalText,
            true,
            [
              { key: 'CASE ID', value: caseId.includes("_") ? caseId.substring(0, caseId.indexOf("_")) : caseId },
              { key: 'PATIENT SURNAME', value: caseData.patientInfo?.lastName || '' },
              { key: 'SURGEON', value: users.find((u) => u.uid === surgeon.userId)?.fullName || '' },
            ]
          ));

          dispatch(
            createActivity(recipient.uid, withCaseActivityCompany(caseData, {
              ...caseNotifications[1].success,
              caseId,
              date: moment().format(),
              userId: currentUser?.uid || '',
            }, state))
          );
        }
      });

      users.filter((u) =>
        isCountryAdminForCase(u, caseData, state) ||
        isDistributorAdminForCase(u, caseData, state)
      )
        .forEach((u) => {
          dispatch(
            createActivity(u.uid, withCaseActivityCompany(caseData, {
              ...caseNotifications[1].success,
              caseId,
              date: moment().format(),
              userId: currentUser?.uid || '',
            }, state))
          );
        });
    }

    dispatch(setNotification({ variant: 'success', vertical: 'top', message: alertMessages[1].success }));

    dispatch({
      type: UPDATE_CASE,
      case: { id: caseId, ...stepData, step: 2, access, files: stepData.files.slice() },
    });
  } catch (error) {
    console.error("Error uploading files:", error);
  }
};

export const updateCase = (activeCase, currentStep, newFiles, rejected) => async (dispatch, getState) => {
  const db = Firebase.db;
  const storage = Firebase.storage;
  const state = getState();
  const currentUser = state.user.currentUser;
  const surgeon = activeCase.surgeon || state.cases.activeCase.surgeon;
  const isTrainingMode = activeCase.trainingMode;
  const access = hasAccess(currentUser, surgeon, activeCase.step, activeCase.procedureFlow, isTrainingMode);

  if (!canWriteCaseFiles(activeCase, 'updateCase')) {
    return;
  }

  activeCase = getFirestoreCaseData(activeCase);
  activeCase.company = getCaseCompanyId(activeCase, state);

  let files = activeCase.files.map(({ downloadLink, ...file }) => file);

  dispatch({ type: LOAD_CASES });

  try {
    if (newFiles?.length) {
      const uploadedFileRecords = getUploadedFileRecords(files, newFiles);
      const replacementTypes = getReplacementTypesFromUploadedFiles(files, newFiles);
      const replacedRejectedFiles = removeReplacedRejectedFiles(files, replacementTypes);

      const uploadPromises = newFiles.map((file, index) => {
        const currentFile = uploadedFileRecords[index];
        const type = currentFile?.type;

        if (type) {
          return uploadBytes(ref(storage, `${activeCase.id}/${activeCase.id}_${type}_${file.name}`), file);
        }

        return null;
      });

      await Promise.all(uploadPromises);
      files = replacedRejectedFiles.files;

      await updateDoc(doc(db, CASES_COLLECTION, activeCase.id), {
        ...activeCase,
        files,
        isHydrated: deleteField(),
        surgeon: `/${SURGEONS_COLLECTION}/${activeCase.surgeonId}`,
        previousStep: currentStep,
      });

      try {
        await deleteReplacedRejectedFileStorage(storage, activeCase.id, replacedRejectedFiles.rejectedFiles, files);
      } catch (error) {
        console.error("Error deleting replaced rejected files:", error);
      }
    } else {
      await updateDoc(doc(db, CASES_COLLECTION, activeCase.id), {
        ...activeCase,
        isHydrated: deleteField(),
        surgeon: `/${SURGEONS_COLLECTION}/${activeCase.surgeonId}`,
        updatedAt: moment().format('YYYY-MM-DD hh:mm A'),
      });
    }

    activeCase.files = files;
    dispatch(triggerNotification(activeCase, currentStep, rejected, activeCase.uploadFilesUid, currentUser.uid));
    dispatch({ type: UPDATE_CASE, case: { ...activeCase, access } });
  } catch (error) {
    console.error("Error updating case:", error);
  }
};

export const simpleUpdateCase = (activeCase, newFiles) => async (dispatch, getState) => {
  const db = Firebase.db;
  const storage = Firebase.storage;
  const state = getState();
  const access = activeCase.access;

  if (!canWriteCaseFiles(activeCase, 'simpleUpdateCase')) {
    return;
  }

  activeCase = getFirestoreCaseData(activeCase);
  activeCase.company = getCaseCompanyId(activeCase, state);

  let files = activeCase.files.map(({ downloadLink, ...file }) => file);
  let replacedRejectedFiles = { rejectedFiles: [] };

  try {
    if (newFiles?.length) {
      const uploadedFileRecords = getUploadedFileRecords(files, newFiles);
      const replacementTypes = getReplacementTypesFromUploadedFiles(files, newFiles);
      replacedRejectedFiles = removeReplacedRejectedFiles(files, replacementTypes);

      const uploadPromises = newFiles.map((file, index) => {
        const currentFile = uploadedFileRecords[index];
        const type = currentFile?.type;

        if (type) {
          return uploadBytes(ref(storage, `${activeCase.id}/${activeCase.id}_${type}_${file.name}`), file);
        }

        return null;
      });

      await Promise.all(uploadPromises);
      files = replacedRejectedFiles.files;
    }

    await Promise.all(
      files.map(async (file) => {
        if (!file.downloadLink) {
          const currentFile = activeCase.files.find((item) => item.name === file.name);
          const type = currentFile?.type;

          file.downloadLink = await Firebase.getDownloadLink(`${activeCase.id}/${activeCase.id}_${type}_${file.name}`) ?? null;
        }
      })
    );

    const docData = {
      ...activeCase,
      files,
      isHydrated: deleteField(),
      surgeon: `/${SURGEONS_COLLECTION}/${activeCase.surgeonId}`,
    };

    await updateDoc(doc(db, CASES_COLLECTION, activeCase.id), docData);
    try {
      await deleteReplacedRejectedFileStorage(storage, activeCase.id, replacedRejectedFiles.rejectedFiles, files);
    } catch (error) {
      console.error("Error deleting replaced rejected files:", error);
    }

    activeCase.files = files;
    dispatch({ type: SET_ACTIVE_CASE, case: { ...activeCase, access } });
  } catch (error) {
    console.error("Error updating case:", error);
  }
};

export const deleteCaseFile = (activeCase, fileId) => async (dispatch) => {
  const db = Firebase.db;
  const storage = Firebase.storage;

  if (!canWriteCaseFiles(activeCase, 'deleteCaseFile')) {
    return;
  }

  const files = activeCase.files.filter((file) => file.id !== fileId);

  try {
    await deleteCaseStorageFile(storage, getCaseStoragePath(activeCase.id, fileId));

    activeCase = getFirestoreCaseData(activeCase);

    const _files = files.map((f) => ({ ...f, downloadLink: f.downloadLink || null }));
    await setDoc(doc(db, CASES_COLLECTION, activeCase.id), { files: _files, isHydrated: deleteField() }, { merge: true });
    dispatch({ type: UPDATE_CASE, case: { ...activeCase, files: _files } });
  } catch (error) {
    console.error("Error deleting case file:", error);
  }
};

export const updateCaseDoc = (caseId, data) => async () => {
  const db = Firebase.db;

  try {
    await setDoc(doc(db, CASES_COLLECTION, caseId), getFirestoreCaseData(data, { deleteHydrationField: true }), { merge: true });
  } catch (error) {
    console.error("Error updating case document:", error);
  }
};

export const requestCaseDateUpdate = (activeCase, date, hospital) => async (dispatch, getState) => {
  const state = getState();
  const { db } = Firebase;
  const { users, surgeons, distributors, teams } = state;
  const currentUser = state.user.currentUser;

  const requestedSurgeryDate = {
    currentDate: activeCase.date ? moment(activeCase.date).format() : null,
    newDate: date?.format(),
    userId: currentUser.uid,
    userName: `${currentUser.firstName} ${currentUser.lastName}`,
  };

  if (hospital) {
    requestedSurgeryDate.currentHospital = activeCase.hospital;
    requestedSurgeryDate.newHospital = hospital;
  }

  try {
    await setDoc(doc(db, CASES_COLLECTION, activeCase.id), { requestedSurgeryDate, isHydrated: deleteField() }, { merge: true });

    const trainingMode = state.sidebar.trainingMode || activeCase.trainingMode;

    if (!trainingMode) {
      const distributor = distributors.list.find((d) => d.id === activeCase.distributorId);
      const caseContext = { ...activeCase, country: activeCase.country || distributor?.country };
      const surgeonName = getSurgeonName(activeCase, surgeons.list, users.list);
      const engineerRecipientIds = new Set(
        getEngineerRecipientsFromTeams(activeCase, users.list, teams.list).map((user) => user.uid)
      );

      users.list.forEach((user) => {
        if (
          user.role === userRoles.GLOBAL_ADMIN.name ||
          isCountryAdminForCase(user, caseContext, state) ||
          isDistributorAdminForCase(user, caseContext, state) ||
          (user.role === userRoles.ENGINEER.name && user.engineerLevel === 1 && engineerRecipientIds.has(user.uid))
        ) {
          const parameters = [
            { key: 'CASE ID', value: activeCase?.formattedId || activeCase?.id },
            { key: 'PATIENT SURNAME', value: getCasePatientLastName(activeCase) },
            { key: 'SURGEON', value: surgeonName },
            { key: 'CURRENT DOS', value: activeCase.date ? moment(activeCase.date).format('DD/MM/YY') : 'TBC' },
            { key: 'NEW DOS', value: date ? moment(date).format('DD/MM/YY') : 'TBC' },
            { key: 'HOSPITAL', value: activeCase.hospital },
          ];

          if (hospital?.name) {
            parameters.push({ key: 'NEW HOSPITAL', value: hospital.name });
          }

          if (!user.disabled) {
            dispatch(
              sendCaseEmail(
                user.email,
                'Request for case date change',
                'Request to change case date has been submitted.',
                `User Requesting change: ${currentUser.firstName} ${currentUser.lastName}.`,
                true,
                parameters
              )
            );

            dispatch(
              createActivity(user.uid, withCaseActivityCompany(caseContext, {
                type: 'notification',
                title: 'Case Date Change Requested',
                text: 'User has requested a change to the case date',
                caseId: activeCase.id,
                date: moment().format(),
                userId: currentUser.uid || '',
              }, state))
            );
          }
        }
      });
    }
  } catch (error) {
    console.error("Error requesting case date update:", error);
  }
};

export const postponeCase = (activeCase) => async (dispatch, getState) => {
  const state = getState();
  const { db } = Firebase;
  const { users, surgeons, teams, manufacturers, logistics } = state;

  const now = moment().format();
  const userId = state.user.currentUser.uid;

  const activity = [
    {
      type: 'activity',
      title: 'Case Postponed',
      text: 'New scans are now available for review and approval',
      date: now,
      userId,
    },
    ...activeCase.activity,
  ];

  try {
    await setDoc(doc(db, CASES_COLLECTION, activeCase.id), { postponed: true, activity, isHydrated: deleteField() }, { merge: true });
    dispatch({
      type: UPDATE_CASE,
      case: {
        ...activeCase,
        postponed: true,
        activity,
      },
    });
    updateCaseStatsOptimistically(dispatch, getState, (stats) => ({
      ...stats,
      totalCases: Math.max(0, stats.totalCases - (activeCase?.archived || activeCase?.postponed ? 0 : 1)),
      futureCases: Math.max(0, stats.futureCases - (isFutureCaseDate(activeCase?.date) && !activeCase?.archived && !activeCase?.postponed ? 1 : 0)),
      postponedCases: stats.postponedCases + (activeCase?.archived || activeCase?.postponed ? 0 : 1),
    }));
    scheduleCaseStatsRefresh(dispatch);
    const trainingMode = state.sidebar.trainingMode || activeCase.trainingMode;

    if (!trainingMode) {
      const surgeon = surgeons.list.find((s) => s.id === activeCase.surgeon.id);
      const surgeonUser = users.list.find((u) => u.uid === surgeon?.userId);
      const engineerRecipientIds = new Set(
        getEngineerRecipientsFromTeams(activeCase, users.list, teams.list).map((user) => user.uid)
      );
      const manufacturerRecipientIds = new Set(
        getTeamRecipientsById(activeCase?.manufacturerId, users.list, manufacturers.list, userRoles.MANUFACTURER.name).map((user) => user.uid)
      );
      const logisticsRecipientIds = new Set(
        getTeamRecipientsById(activeCase?.logisticsId, users.list, logistics.list, userRoles.LOGISTICS.name).map((user) => user.uid)
      );

      users.list.forEach((user) => {
        if (
          (user.role === userRoles.ENGINEER.name && engineerRecipientIds.has(user.uid)) ||
          (user.role === userRoles.SALES_REP.name && surgeon?.salesRep?.includes(user.uid)) ||
          (user.role === userRoles.PRACTICE_MANAGER.name && surgeon?.manager?.includes(user.uid)) ||
          (user.role === userRoles.REGISTRAR.name && surgeon?.registrar?.includes(user.uid)) ||
          (user.role === userRoles.MANUFACTURER.name && manufacturerRecipientIds.has(user.uid)) ||
          (user.role === userRoles.LOGISTICS.name && logisticsRecipientIds.has(user.uid))
        ) {
          const parameters = [
            { key: 'CASE ID', value: activeCase?.formattedId || activeCase?.id },
            { key: 'PATIENT REFERENCE', value: activeCase.patientInfo?.lastName || '' },
            { key: 'SURGEON', value: surgeonUser ? `${surgeonUser.firstName} ${surgeonUser.lastName}` : '' },
            { key: 'PREVIOUS DOS', value: activeCase?.date ? moment(activeCase.date).format('DD/MM/YY') : 'TBC' },
            { key: 'HOSPITAL', value: activeCase.hospital },
          ];

          if (user.uid !== activeCase?.requestedSurgeryDateUserId && !user?.disabled) {
            dispatch(
              sendCaseEmail(
                user.email,
                'Case Postponed',
                'The following case has been updated to Postponed and removed from the active cases list. The previously scheduled surgery date is set to TBC until a future date is defined.',
                '',
                true,
                parameters
              )
            );
          }

          dispatch(
            createActivity(user.uid, withCaseActivityCompany(activeCase, {
              type: 'activity',
              title: 'Case Postponed',
              text: 'A surgery date for the following case has been postponed.',
              caseId: activeCase.id,
              date: moment().format(),
              userId: user?.uid || '',
            }, state))
          );
        }
      });
    }
  } catch (error) {
    console.error("Error approving request date update:", error);
  }
};

export const approveRequestDateUpdate = (activeCase) => async (dispatch, getState) => {
  const state = getState();
  const { db } = Firebase;
  const { users, surgeons, hospitals, teams, manufacturers, logistics } = state;

  const docData = {
    date: toFirestoreDateString(activeCase?.requestedSurgeryDate?.newDate || activeCase?.requestedSurgeryDateNew),
    requestedSurgeryDate: null,
    postponed: false,
    isHydrated: deleteField(),
  };

  // const newHospital = activeCase?.requestedSurgeryDate?.newHospital?.id ? activeCase?.requestedSurgeryDate?.newHospital : hospitals.find((h) => h.id === activeCase?.requestedSurgeryDate?.newHospital);
  const newHospital = activeCase?.requestedSurgeryDate?.newHospital?.id ? activeCase?.requestedSurgeryDate?.newHospital : hospitals?.list?.find((h) => h.id === activeCase?.requestedSurgeryDateNewHospital || h.name === activeCase?.requestedSurgeryDateNewHospital);
  if (newHospital) {
    docData.hospital = newHospital?.name || '';
    docData.hospitalUniq = {
      id: newHospital?.id || '',
      name: newHospital?.name || '',
    };
  }

  try {
    await setDoc(doc(db, CASES_COLLECTION, activeCase.id), docData, { merge: true });

    const trainingMode = state.sidebar.trainingMode || activeCase.trainingMode;

    if (!trainingMode) {
      const requestUser = users.list.find((u) => u.uid === activeCase?.requestedSurgeryDate?.userId || u.uid === activeCase?.requestedSurgeryDateUserId);
      const surgeon = getCaseSurgeon(activeCase, surgeons.list);
      const surgeonName = getSurgeonName(activeCase, surgeons.list, users.list);
      const engineerRecipientIds = new Set(
        getEngineerRecipientsFromTeams(activeCase, users.list, teams.list).map((user) => user.uid)
      );
      const manufacturerRecipientIds = new Set(
        getTeamRecipientsById(activeCase?.manufacturerId, users.list, manufacturers.list, userRoles.MANUFACTURER.name).map((user) => user.uid)
      );
      const logisticsRecipientIds = new Set(
        getTeamRecipientsById(activeCase?.logisticsId, users.list, logistics.list, userRoles.LOGISTICS.name).map((user) => user.uid)
      );

      if (requestUser?.email && !requestUser?.disabled) {
        const parameters = [
          { key: 'CASE ID', value: activeCase?.formattedId || activeCase?.id },
          { key: 'PATIENT SURNAME', value: getCasePatientLastName(activeCase) },
          { key: 'SURGEON', value: surgeonName },
          { key: newHospital ? 'NEW HOSPITAL' : 'HOSPITAL', value: newHospital ? newHospital.name : activeCase.hospital },
          { key: 'NEW DOS', value: docData.date ? moment(docData.date).format('DD/MM/YY') : 'TBC' },
        ];

        dispatch(
          sendCaseEmail(
            requestUser.email,
            'Case Date Change Request: APPROVED',
            'Your request to change the surgery date of the following case has been: APPROVED.',
            'Should you have any questions relating to this change, please contact Personalised Surgery.',
            true,
            parameters
          )
        );
      }

      users.list.forEach((user) => {
        if (
          (user.role === userRoles.ENGINEER.name && engineerRecipientIds.has(user.uid)) ||
          (user.role === userRoles.SALES_REP.name && surgeon?.salesRep?.includes(user.uid)) ||
          (user.role === userRoles.PRACTICE_MANAGER.name && surgeon?.manager?.includes(user.uid)) ||
          (user.role === userRoles.LOGISTICS.name && logisticsRecipientIds.has(user.uid)) ||
          (user.role === userRoles.MANUFACTURER.name && manufacturerRecipientIds.has(user.uid)) ||
          (user.role === userRoles.REGISTRAR.name && surgeon?.registrar?.includes(user.uid))
        ) {
          const parameters = [
            { key: 'CASE NUMBER', value: activeCase?.formattedId || activeCase?.id },
            { key: 'PATIENT REFERENCE', value: getCasePatientLastName(activeCase) },
            { key: 'SURGEON', value: surgeonName },
            { key: newHospital ? 'NEW HOSPITAL' : 'HOSPITAL', value: newHospital ? newHospital.name : activeCase.hospital },
            { key: 'NEW CASE DATE', value: docData.date ? moment(docData.date).format('DD/MM/YY') : 'TBC' },
          ];

          if (user.uid !== activeCase?.requestedSurgeryDateUserId && !user?.disabled) {
            dispatch(
              sendCaseEmail(
                user.email,
                'Case Date Change Request: APPROVED',
                'A new requested surgery date for the following case has been approved.',
                'Should you have any questions relating to this change, please contact Personalised Surgery.',
                true,
                parameters
              )
            );
          }
        }
      });
    }
  } catch (error) {
    console.error("Error approving request date update:", error);
  }
};

export const rejectRequestDateUpdate = (activeCase) => async (dispatch, getState) => {
  const state = getState();
  const { db } = Firebase;
  const { users, surgeons } = state;

  const docData = { requestedSurgeryDate: null, isHydrated: deleteField() };

  try {
    await setDoc(doc(db, CASES_COLLECTION, activeCase.id), docData, { merge: true });

    const trainingMode = state.sidebar.trainingMode || activeCase.trainingMode;

    if (!trainingMode) {
      const requestUser = users.list.find((u) => u.uid === activeCase?.requestedSurgeryDate?.userId || u.uid === activeCase?.requestedSurgeryDateUserId);
      const surgeonName = getSurgeonName(activeCase, surgeons.list, users.list);

      if (requestUser?.email && !requestUser?.disabled) {
        const parameters = [
          { key: 'CASE ID', value: activeCase?.formattedId || activeCase?.id },
          { key: 'PATIENT SURNAME', value: getCasePatientLastName(activeCase) },
          { key: 'SURGEON', value: surgeonName },
          { key: 'HOSPITAL', value: activeCase.hospital },
          { key: 'CURRENT DOS', value: activeCase.date ? moment(activeCase.date).format('DD/MM/YY') : 'TBC' },
        ];

        dispatch(
          sendCaseEmail(
            requestUser.email,
            'Case Date Change Request: REJECTED',
            'Your request to change the surgery date of the following case has been: REJECTED.',
            'Personalised Surgery Admin team have rejected this change for specific operational or logistics reasons. Please contact the team to discuss further.',
            true,
            parameters
          )
        );
      }
    }
  } catch (error) {
    console.error("Error rejecting request date update:", error);
  }
};

export const requestConsultationDateUpdate = (activeCase, date) => async (dispatch, getState) => {
  const state = getState();
  const { db } = Firebase;
  const { users, surgeons, distributors, teams } = state;
  const currentUser = state.user.currentUser;

  const requestedConsultationDate = {
    currentDate: activeCase.consultationDate ? moment(activeCase.consultationDate).format() : null,
    newDate: date?.format(),
    userId: currentUser.uid,
    userName: `${currentUser.firstName} ${currentUser.lastName}`,
  };

  try {
    await setDoc(doc(db, CASES_COLLECTION, activeCase.id), { requestedConsultationDate, isHydrated: deleteField() }, { merge: true });

    const trainingMode = state.sidebar.trainingMode || activeCase.trainingMode;

    if (!trainingMode) {
      const distributor = distributors.list.find((d) => d.id === activeCase.distributorId);
      const caseContext = { ...activeCase, country: activeCase.country || distributor?.country };
      const surgeonName = getSurgeonName(activeCase, surgeons.list, users.list);
      const engineerRecipientIds = new Set(
        getEngineerRecipientsFromTeams(activeCase, users.list, teams.list).map((user) => user.uid)
      );

      users.list.forEach((user) => {
        if (
          user.role === userRoles.GLOBAL_ADMIN.name ||
          isCountryAdminForCase(user, caseContext, state) ||
          isDistributorAdminForCase(user, caseContext, state) ||
          (user.role === userRoles.ENGINEER.name && user.engineerLevel === 1 && engineerRecipientIds.has(user.uid))
        ) {
          const parameters = [
            { key: 'CASE ID', value: activeCase?.formattedId || activeCase?.id },
            { key: 'PATIENT SURNAME', value: getCasePatientLastName(activeCase) },
            { key: 'SURGEON', value: surgeonName },
            { key: 'CURRENT DATE', value: activeCase.consultationDate ? moment(activeCase.consultationDate).format('DD/MM/YY') : 'TBC' },
            { key: 'NEW DATE', value: date ? moment(date).format('DD/MM/YY') : 'TBC' },
            { key: 'HOSPITAL', value: activeCase.hospital },
          ];

          if (!user.disabled) {
            dispatch(
              sendCaseEmail(
                user.email,
                'Request for consultation date change',
                'Request to change consultation date has been submitted.',
                `User Requesting change: ${currentUser.firstName} ${currentUser.lastName}.`,
                true,
                parameters
              )
            );

            dispatch(
              createActivity(user.uid, withCaseActivityCompany(caseContext, {
                type: 'notification',
                title: 'Consultation Date Change Requested',
                text: 'User has requested a change to the consultation date',
                caseId: activeCase.id,
                date: moment().format(),
                userId: currentUser.uid || '',
              }, state))
            );
          }
        }
      });
    }
  } catch (error) {
    console.error("Error requesting consultation date update:", error);
    throw error;
  }
};

export const approveRequestConsultationDateUpdate = (activeCase) => async (dispatch, getState) => {
  const state = getState();
  const { db } = Firebase;
  const { users, surgeons, teams, manufacturers, logistics } = state;

  const docData = {
    consultationDate: toFirestoreDateString(activeCase?.requestedConsultationDate?.newDate || activeCase?.requestedConsultationDateNew),
    requestedConsultationDate: null,
    isHydrated: deleteField(),
  };

  try {
    await setDoc(doc(db, CASES_COLLECTION, activeCase.id), docData, { merge: true });

    const trainingMode = state.sidebar.trainingMode || activeCase.trainingMode;

    if (!trainingMode) {
      const requestUser = users.list.find((u) => u.uid === activeCase?.requestedConsultationDate?.userId || u.uid === activeCase?.requestedConsultationDateUserId);
      const surgeon = getCaseSurgeon(activeCase, surgeons.list);
      const surgeonName = getSurgeonName(activeCase, surgeons.list, users.list);
      const engineerRecipientIds = new Set(
        getEngineerRecipientsFromTeams(activeCase, users.list, teams.list).map((user) => user.uid)
      );
      const manufacturerRecipientIds = new Set(
        getTeamRecipientsById(activeCase?.manufacturerId, users.list, manufacturers.list, userRoles.MANUFACTURER.name).map((user) => user.uid)
      );
      const logisticsRecipientIds = new Set(
        getTeamRecipientsById(activeCase?.logisticsId, users.list, logistics.list, userRoles.LOGISTICS.name).map((user) => user.uid)
      );

      if (requestUser?.email && !requestUser?.disabled) {
        const parameters = [
          { key: 'CASE ID', value: activeCase?.formattedId || activeCase?.id },
          { key: 'PATIENT SURNAME', value: getCasePatientLastName(activeCase) },
          { key: 'SURGEON', value: surgeonName },
          { key: 'NEW DATE', value: docData.consultationDate ? moment(docData.consultationDate).format('DD/MM/YY') : 'TBC' },
        ];

        dispatch(
          sendCaseEmail(
            requestUser.email,
            'Consultation Date Change Request: APPROVED',
            'Your request to change the consultation date of the following case has been: APPROVED.',
            'Should you have any questions relating to this change, please contact Personalised Surgery.',
            true,
            parameters
          )
        );
      }

      users.list.forEach((user) => {
        if (
          (user.role === userRoles.ENGINEER.name && engineerRecipientIds.has(user.uid)) ||
          (user.role === userRoles.SALES_REP.name && surgeon?.salesRep?.includes(user.uid)) ||
          (user.role === userRoles.PRACTICE_MANAGER.name && surgeon?.manager?.includes(user.uid)) ||
          (user.role === userRoles.LOGISTICS.name && logisticsRecipientIds.has(user.uid)) ||
          (user.role === userRoles.MANUFACTURER.name && manufacturerRecipientIds.has(user.uid)) ||
          (user.role === userRoles.REGISTRAR.name && surgeon?.registrar?.includes(user.uid))
        ) {
          const parameters = [
            { key: 'CASE NUMBER', value: activeCase?.formattedId || activeCase?.id },
            { key: 'PATIENT REFERENCE', value: getCasePatientLastName(activeCase) },
            { key: 'SURGEON', value: surgeonName },
            { key: 'NEW DATE', value: docData.consultationDate ? moment(docData.consultationDate).format('DD/MM/YY') : 'TBC' },
          ];

          if (
            user.uid !== activeCase?.requestedConsultationDate?.userId &&
            user.uid !== activeCase?.requestedConsultationDateUserId &&
            !user?.disabled
          ) {
            dispatch(
              sendCaseEmail(
                user.email,
                'Consultation Date Change Request: APPROVED',
                'A new requested consultation date for the following case has been approved.',
                'Should you have any questions relating to this change, please contact Personalised Surgery.',
                true,
                parameters
              )
            );
          }
        }
      });
    }
  } catch (error) {
    console.error("Error approving request consultation date update:", error);
    throw error;
  }
};

export const rejectRequestConsultationDateUpdate = (activeCase) => async (dispatch, getState) => {
  const state = getState();
  const { db } = Firebase;
  const { users, surgeons } = state;

  const docData = { requestedConsultationDate: null, isHydrated: deleteField() };

  try {
    await setDoc(doc(db, CASES_COLLECTION, activeCase.id), docData, { merge: true });

    const trainingMode = state.sidebar.trainingMode || activeCase.trainingMode;

    if (!trainingMode) {
      const requestUser = users.list.find((u) => u.uid === activeCase?.requestedConsultationDate?.userId || u.uid === activeCase?.requestedConsultationDateUserId);
      const surgeonName = getSurgeonName(activeCase, surgeons.list, users.list);

      if (requestUser?.email && !requestUser?.disabled) {
        const parameters = [
          { key: 'CASE ID', value: activeCase?.formattedId || activeCase?.id },
          { key: 'PATIENT SURNAME', value: getCasePatientLastName(activeCase) },
          { key: 'SURGEON', value: surgeonName },
          { key: 'HOSPITAL', value: activeCase.hospital },
          { key: 'CURRENT DATE', value: activeCase.consultationDate ? moment(activeCase.consultationDate).format('DD/MM/YY') : 'TBC' },
        ];

        dispatch(
          sendCaseEmail(
            requestUser.email,
            'Consultation Date Change Request: REJECTED',
            'Your request to change the consultation date of the following case has been: REJECTED.',
            'Personalised Surgery Admin team have rejected this change for specific operational or logistics reasons. Please contact the team to discuss further.',
            true,
            parameters
          )
        );
      }
    }
  } catch (error) {
    console.error("Error rejecting request consultation date update:", error);
    throw error;
  }
};

export const sendChangeDateHospitalEmail = (id, data, previousData) => async (dispatch, getState) => {
  const state = getState();
  const { users, surgeons, teams, manufacturers, logistics } = state;
  const currentUser = state.user.currentUser;

  const caseData = { ...previousData, ...data };
  const surgeon = getCaseSurgeon(caseData, surgeons.list);
  const surgeonUser = users.list.find((user) => user.uid === surgeon?.userId);
  const surgeonName = getSurgeonName(caseData, surgeons.list, users.list);

  let emails = casesEmails;
  if (caseData.procedureFlow === procedureFlows.ACL) {
    emails = casesACLEmails;
  } else if (caseData.procedureFlow === procedureFlows.CMF) {
    emails = casesCMFEmails;
  } else if (cmfExtendedProcedureFlows.includes(caseData.procedureFlow)) {
    emails = casesCMFExtendedEmails;
  }

  const dateEmail = emails['0'].updateDate;
  const hospitalEmail = emails['0'].updateHospital;

  const trainingMode = state.sidebar.trainingMode || previousData.trainingMode;

  if (!trainingMode) {
    if (data.date !== previousData.date) {
      const recipients = getRecipientsForRoles(dateEmail.roles, surgeon, caseData, users.list, teams.list, manufacturers.list, logistics.list);

      const parameters = [
        { key: 'CASE ID', value: id.includes("_") ? id.substring(0, id.indexOf("_")) : id },
        { key: 'PATIENT SURNAME', value: getCasePatientLastName(caseData) },
        { key: 'SURGEON', value: surgeonName },
        { key: 'HOSPITAL', value: data.hospital },
        {
          key: 'OLD DOS',
          value: previousData.postponed ? 'Postponed' : (
            previousData.date ? moment(previousData.date).format('DD/MM/YY') : 'TBC'
          ),
        },
        { key: 'NEW DOS', value: data.date ? moment(data.date).format('DD/MM/YY') : 'TBC' },
      ];

      recipients.forEach((recipient) => {
        if (recipient?.uid !== currentUser?.uid && !recipient?.disabled) {
          dispatch(
            sendCaseEmail(
              recipient.email,
              dateEmail.subject,
              dateEmail.message({ caseId: id.includes("_") ? id.substring(0, id.indexOf("_")) : id }),
              dateEmail.additionalText({
                caseId: id.includes("_") ? id.substring(0, id.indexOf("_")) : id,
                oldDate: previousData.postponed ? 'Postponed' : (
                  previousData.date ? moment(previousData.date).format('DD/MM/YY') : 'TBC'
                ),
                newDate: data.date ? moment(data.date).format('DD/MM/YY') : 'TBC'
              }),
              true,
              parameters
            )
          );
        }
      });
    }

    if (data.hospital !== previousData.hospital) {
      const recipients = uniqByUid([
        surgeonUser,
        ...getRecipientsForRoles(hospitalEmail.roles, surgeon, caseData, users.list, teams.list, manufacturers.list, logistics.list),
      ]);

      const parameters = [
        { key: 'CASE ID', value: id.includes("_") ? id.substring(0, id.indexOf("_")) : id },
        { key: 'PATIENT SURNAME', value: getCasePatientLastName(caseData) },
        { key: 'SURGEON', value: surgeonName },
        { key: 'DOS', value: data.date ? moment(data.date).format('DD/MM/YY') : 'TBC' },
        { key: 'OLD HOSPITAL', value: previousData.hospital },
        { key: 'NEW HOSPITAL', value: data.hospital },
      ];

      recipients.forEach((recipient) => {
        if (recipient?.uid !== currentUser?.uid && !recipient?.disabled) {
          dispatch(
            sendCaseEmail(
              recipient.email,
              hospitalEmail.subject,
              hospitalEmail.message({ caseId: id.includes("_") ? id.substring(0, id.indexOf("_")) : id }),
              hospitalEmail.additionalText({ caseId: id.includes("_") ? id.substring(0, id.indexOf("_")) : id }),
              true,
              parameters
            )
          );
        }
      });
    }
  }
};

export const changePreopFile = (activeCase, file) => async (dispatch, getState) => {
  const state = getState();
  const { db, storage } = Firebase;
  const userId = state.user.currentUser.uid;

  if (!canWriteCaseFiles(activeCase, 'changePreopFile')) {
    return;
  }

  const oldPreop = activeCase.files.find((item) => item.type === fileTypes.preop);
  const preopIndex = activeCase.files.findIndex((item) => item.type === fileTypes.preop);

  if (!oldPreop || preopIndex === -1) {
    console.error('Pre op file not found for replacement');
    return;
  }

  const files = activeCase.files.map((item) => ({
    id: item.id,
    date: item.date,
    name: item.name,
    status: item.status || fileStatuses.PENDING,
    type: item.type,
    userId: item.userId,
  }));

  files[preopIndex] = {
    id: `${activeCase.id}_${fileTypes.preop}_${file.name}`,
    name: file.name,
    type: fileTypes.preop,
    status: fileStatuses.APPROVED,
    date: moment().format(),
    userId,
  };

  try {
    const oldPath = getCaseStoragePath(activeCase.id, oldPreop.id);
    const newPath = getCaseStoragePath(activeCase.id, `${activeCase.id}_${fileTypes.preop}_${file.name}`);
    await replaceCaseStorageFile(storage, oldPath, newPath, file, () =>
      updateDoc(doc(db, CASES_COLLECTION, activeCase.id), {
        ...getFirestoreCaseData(activeCase),
        files,
        isHydrated: deleteField(),
        surgeon: `/${SURGEONS_COLLECTION}/${activeCase.surgeonId}`,
      })
    );

    dispatch(
      setNotification({
        variant: 'success',
        vertical: 'top',
        message: 'Pre op file has been changed',
      })
    );
  } catch (error) {
    console.error('Error changing preop file:', error);
  }
};

export const changeAlignmentAnalysisFile = (activeCase, file) => async (dispatch, getState) => {
  const state = getState();
  const { db, storage } = Firebase;
  const userId = state.user.currentUser.uid;

  if (!canWriteCaseFiles(activeCase, 'changeAlignmentAnalysisFile')) {
    return;
  }

  const oldFile = activeCase.files.find((item) => item.type === fileTypes.alignmentAnalysis);
  const fileIndex = activeCase.files.findIndex((item) => item.type === fileTypes.alignmentAnalysis);

  if (!oldFile || fileIndex === -1) {
    console.error('Alignment analysis file not found for replacement');
    return;
  }

  const files = activeCase.files.map((item) => ({
    id: item.id,
    date: item.date,
    name: item.name,
    status: item.status || fileStatuses.PENDING,
    type: item.type,
    userId: item.userId,
  }));

  files[fileIndex] = {
    id: `${activeCase.id}_${fileTypes.alignmentAnalysis}_${file.name}`,
    name: file.name,
    type: fileTypes.alignmentAnalysis,
    status: fileStatuses.APPROVED,
    date: moment().format(),
    userId,
  };

  try {
    const oldPath = getCaseStoragePath(activeCase.id, oldFile.id);
    const newPath = getCaseStoragePath(activeCase.id, `${activeCase.id}_${fileTypes.alignmentAnalysis}_${file.name}`);
    await replaceCaseStorageFile(storage, oldPath, newPath, file, () =>
      setDoc(doc(db, CASES_COLLECTION, activeCase.id), { files, isHydrated: deleteField() }, { merge: true })
    );

    dispatch(
      setNotification({
        variant: 'success',
        vertical: 'top',
        message: 'Alignment analysis file has been changed',
      })
    );
  } catch (error) {
    console.error('Error changing alignment analysis file:', error);
  }
};

export const changeDesignReportFile = (activeCase, file) => async (dispatch, getState) => {
  const state = getState();
  const { db, storage } = Firebase;
  const userId = state.user.currentUser.uid;

  if (!canWriteCaseFiles(activeCase, 'changeDesignReportFile')) {
    return;
  }

  const oldFile = activeCase.files.find((item) => item.type === fileTypes.designReport);
  const fileIndex = activeCase.files.findIndex((item) => item.type === fileTypes.designReport);

  if (!oldFile || fileIndex === -1) {
    console.error('Design report file not found for replacement');
    return;
  }

  const files = activeCase.files.map((item) => ({
    id: item.id,
    date: item.date,
    name: item.name,
    status: item.status || fileStatuses.PENDING,
    type: item.type,
    userId: item.userId,
  }));

  files[fileIndex] = {
    id: `${activeCase.id}_${fileTypes.designReport}_${file.name}`,
    name: file.name,
    type: fileTypes.designReport,
    status: fileStatuses.APPROVED,
    date: moment().format(),
    userId,
  };

  try {
    const oldPath = getCaseStoragePath(activeCase.id, oldFile.id);
    const newPath = getCaseStoragePath(activeCase.id, `${activeCase.id}_${fileTypes.designReport}_${file.name}`);
    await replaceCaseStorageFile(storage, oldPath, newPath, file, () =>
      setDoc(doc(db, CASES_COLLECTION, activeCase.id), { files, isHydrated: deleteField() }, { merge: true })
    );

    dispatch(
      setNotification({
        variant: 'success',
        vertical: 'top',
        message: 'Design Report file has been changed',
      })
    );
  } catch (error) {
    console.error('Error changing design report file:', error);
  }
};

export const changePlanReportFile = (activeCase, file) => async (dispatch, getState) => {
  const state = getState();
  const { db, storage } = Firebase;
  const userId = state.user.currentUser.uid;

  if (!canWriteCaseFiles(activeCase, 'changePlanReportFile')) {
    return;
  }

  const oldFile = activeCase.files.find((item) => item.type === fileTypes.planReport);
  const fileIndex = activeCase.files.findIndex((item) => item.type === fileTypes.planReport);

  if (!oldFile || fileIndex === -1) {
    console.error('Planning report file not found for replacement');
    return;
  }

  const files = activeCase.files.map((item) => ({
    id: item.id,
    date: item.date,
    name: item.name,
    status: item.status || fileStatuses.PENDING,
    type: item.type,
    userId: item.userId,
  }));

  files[fileIndex] = {
    id: `${activeCase.id}_${fileTypes.planReport}_${file.name}`,
    name: file.name,
    type: fileTypes.planReport,
    status: fileStatuses.APPROVED,
    date: moment().format(),
    userId,
  };

  try {
    const oldPath = getCaseStoragePath(activeCase.id, oldFile.id);
    const newPath = getCaseStoragePath(activeCase.id, `${activeCase.id}_${fileTypes.planReport}_${file.name}`);
    await replaceCaseStorageFile(storage, oldPath, newPath, file, () =>
      setDoc(doc(db, CASES_COLLECTION, activeCase.id), { files, isHydrated: deleteField() }, { merge: true })
    );

    dispatch(
      setNotification({
        variant: 'success',
        vertical: 'top',
        message: 'Planning report file has been changed',
      })
    );
  } catch (error) {
    console.error('Error changing planning report file:', error);
  }
};

export const confirmPlanningMeeting = (activeCase, planning) => async (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user.currentUser;
  const users = state.users.list;
  const surgeon = activeCase.surgeon || state.cases.activeCase.surgeon;
  const surgeonUser = users?.find((u) => u.uid === surgeon.userId);

  if (activeCase.sortDate) {
    delete activeCase.sortDate;
  }

  if (activeCase.caseNotes) {
    delete activeCase.caseNotes;
  }

  try {
    await dispatch(simpleUpdateCase({ ...activeCase, planning }));

    const email = casesCMFExtendedEmails[3].planningConfirmed;
    const caseId = activeCase?.id?.includes('_') ? activeCase.id.substring(0, activeCase.id.indexOf('_')) : activeCase.id;
    const planningLink = planning.meetingLink || '';
    const planningDate = planning?.requestPlanningDate ? moment(activeCase?.planning?.requestPlanningDate).format('DD/MM/YYYY, h:mm a') : '';

    if (surgeonUser && surgeonUser.uid !== currentUser?.uid && !surgeonUser?.disabled) {
      await dispatch(
        sendCaseEmail(
          surgeonUser.email,
          email.subject(),
          email.message({ ...activeCase, caseId }),
          '',
          true,
          [
            { key: 'MEETING LINK', value: planningLink },
            { key: 'DATE/TIME', value: planningDate },
          ]
        )
      );

      dispatch(
        createActivity(surgeonUser.uid, withCaseActivityCompany(activeCase, {
          type: 'activity',
          title: 'Planning Meeting Confirmed',
          text: 'A requested planning meeting has now been confirmed',
          caseId: activeCase.id,
          date: moment().format(),
          userId: currentUser?.uid || '',
        }, state))
      );
    }
  } catch (error) {
    console.error('Error confirming planning meeting:', error);
  }
};

const triggerNotification = (activeCase, currentStep, rejected, customRecipientId, userId) => (dispatch, getState) => {
  const state = getState();
  const { users, procedures, teams, manufacturers, logistics } = state;
  const currentUser = state.user.currentUser;
  const surgeon = activeCase.surgeon || state.cases.activeCase.surgeon;
  const surgeonUser = users.list.find((u) => u.uid === surgeon.userId);
  const procedure = procedures.list.find((p) => p.id === activeCase?.procedure);

  const type = rejected ? 'reject' : 'success';

  let notifications = caseNotifications;
  if (activeCase.procedureFlow === procedureFlows.ACL) {
    notifications = caseACLNotifications;
  } else if (activeCase.procedureFlow === procedureFlows.CMF) {
    notifications = caseCMFNotifications;
  } else if (cmfExtendedProcedureFlows.includes(activeCase.procedureFlow)) {
    notifications = caseCMFExtendedNotifications;
  }

  let emails = casesEmails;
  if (activeCase.procedureFlow === procedureFlows.ACL) {
    emails = casesACLEmails;
  } else if (activeCase.procedureFlow === procedureFlows.CMF) {
    emails = casesCMFEmails;
  } else if (cmfExtendedProcedureFlows.includes(activeCase.procedureFlow)) {
    emails = casesCMFExtendedEmails;
  }

  let email = ![procedureFlows.ACL, procedureFlows.CMF, ...cmfExtendedProcedureFlows].includes(activeCase.procedureFlow) && activeCase.step === 7 && currentStep === 5 ?
    casesEmails[5].manufacturer : emails[currentStep]?.[type];

  const parameters = email && email.parameters ? email.parameters.map((key) => {
    switch (key) {
      case 'caseId':
        return { key: 'CASE ID', value: activeCase.formattedId || activeCase.id };
      case 'lastName':
        return { key: 'PATIENT SURNAME', value: activeCase?.patientInfo?.lastName || '' };
      case 'date':
        return { key: 'DOS', value: activeCase.date ? moment(activeCase.date).format('DD/MM/YY') : 'TBC' };
      case 'surgeon':
        return { key: 'SURGEON', value: surgeonUser ? `${surgeonUser.firstName} ${surgeonUser.lastName}` : '' };
      case 'hospital':
        return { key: 'HOSPITAL', value: activeCase.hospital };
      case 'procedure':
        return { key: 'PROCEDURE', value: procedure ? procedure.name : '' };
      case 'planningDate':
        return { key: 'DATE/TIME', value: activeCase?.planning?.requestPlanningDate ? moment(activeCase?.planning?.requestPlanningDate).format('DD/MM/YYYY, h:mm a') : '' };
      case 'planningLink':
        return { key: 'MEETING LINK', value: activeCase?.planning?.meetingLink || '' };
      case 'note':
        return { key: 'NOTE', value: activeCase?.notes?.[`step${currentStep}`] || '' };
      default:
        return { key: '', value: '' };
    }
  }) : [];

  if (!activeCase.trainingMode) {
    if (currentStep === 2 && customRecipientId && rejected) {
      const recipient = users.list.find((user) => user.uid === customRecipientId);

      if (recipient?.uid !== currentUser?.uid && !recipient?.disabled && userMatchesCaseCompany(recipient, activeCase, state)) {
        dispatch(
          sendCaseEmail(
            recipient,
            email.subject,
            email.message({
              ...activeCase,
              caseId: activeCase.formattedId || activeCase.id,
            }),
            email.additionalText,
            true,
            parameters
          )
        );
      }
      if (recipient && userMatchesCaseCompany(recipient, activeCase, state)) {
        dispatch(
          createActivity(recipient.uid, withCaseActivityCompany(activeCase, {
            ...notifications[currentStep][type],
            caseId: activeCase.id,
            date: moment().format(),
            userId: userId || '',
          }, state))
        );
      }
    }

    if ([procedureFlows.ACL, procedureFlows.CMF].includes(activeCase.procedureFlow) && currentStep > 5) {
      email = null;
    }

    if (email && currentStep !== 2) {
      email.roles.forEach((role) => {
        if (cmfExtendedProcedureFlows.includes(activeCase.procedureFlow) && currentStep === 3 && !activeCase.planning?.planApproveRequired) {
          return;
        }

        const recipients = getRoleRecipients(role, surgeon, activeCase, users.list, teams.list, manufacturers.list, logistics.list);

        recipients.forEach((recipient) => {
          if (recipient?.uid !== currentUser?.uid && !recipient?.disabled) {
            dispatch(
              sendCaseEmail(
                recipient,
                email.subject({ ...activeCase, recipient }),
                email.message({
                  ...activeCase,
                  recipient,
                  caseId: activeCase.formattedId || activeCase.id,
                }),
                email.additionalText({ ...activeCase, recipient }),
                true,
                parameters
              )
            );
          }

          dispatch(
            createActivity(recipient.uid, withCaseActivityCompany(activeCase, {
              ...notifications[currentStep][type],
              caseId: activeCase.id,
              date: moment().format(),
              userId: userId || '',
            }, state))
          );
        });
      });
    }
  }

  const engineerRecipientIds = new Set(
    email?.roles?.find((r) => r.name === userRoles.ENGINEER.name)
      ? getEngineerRecipientsFromTeams(activeCase, users.list, teams.list).map((user) => user.uid)
      : []
  );

  users.list?.filter((u) =>
    isCountryAdminForCase(u, activeCase, state) ||
    isDistributorAdminForCase(u, activeCase, state) ||
    (u.role === userRoles.ENGINEER.name && engineerRecipientIds.has(u.uid))
  )
    .forEach((u) => {
      dispatch(
        createActivity(u.uid, withCaseActivityCompany(activeCase, {
          ...notifications[currentStep][type],
          caseId: activeCase.id,
          date: moment().format(),
          userId: userId || '',
        }, state))
      );
    });

  const messages =
    activeCase.procedureFlow === procedureFlows.ACL
      ? alertAclMessages
      : activeCase.procedureFlow === procedureFlows.CMF
        ? alertCmfMessages
        : cmfExtendedProcedureFlows.includes(activeCase.procedureFlow)
          ? alertCmfExtendedMessages
          : alertMessages;

  const message = messages[currentStep][type];

  if (message) {
    dispatch(
      setNotification({
        variant: rejected ? 'warning' : 'success',
        vertical: 'top',
        message,
      })
    );
  }
};

// const onNoteAdded = (activeCase, note) => (dispatch, getState) => {
//   const state = getState();
//   const userId = state.user.currentUser.uid;
//   const surgeon = activeCase.surgeon || state.cases.activeCase.surgeon;
//   const users = state.users.list;
//
//   if (!activeCase.trainingMode) {
//     const recipients = [];
//
//     users?.forEach((user) => {
//       if ([roleNames.GLOBAL_ADMIN, roleNames.COUNTRY_ADMIN, roleNames.DISTRIBUTOR_ADMIN].includes(user.role)) {
//         recipients?.push(user.uid);
//       } else if (user.role === roleNames.SURGEON && surgeon.userId === user.uid) {
//         recipients?.push(user.uid);
//       } else {
//         const roleField = userRoles[ user.role ]?.field || '';
//         const recipientId = surgeon[ roleField ];
//
//         if (( typeof recipientId === 'string' && user.uid === recipientId ) || recipientId?.includes(user.uid)) {
//           recipients?.push(user.uid);
//         }
//       }
//     });
//
//     recipients?.forEach((recipient) => {
//       dispatch(
//         createActivity(recipient.uid, {
//           type: 'notification',
//           title: 'Note Added',
//           text: note,
//           caseId: activeCase.id,
//           date: moment().format(),
//           userId: userId || ''
//         })
//       );
//     });
//   }
// };

export const notifyUsers = (activeCase) => (dispatch, getState) => {
  if (!activeCase) return;

  const state = getState();
  const { users, procedures, teams, manufacturers, logistics } = state;
  const currentUser = state.user.currentUser;
  const surgeon = activeCase.surgeon || state.cases.activeCase.surgeon;
  const type = activeCase.status === statuses.rejected ? 'reject' : 'success';
  const previousStep = activeCase.previousStep || activeCase.step - 1;

  const surgeonUser = users.list.find((u) => u.uid === surgeon.userId);
  const procedure = procedures.list.find((p) => p.id === activeCase?.procedure);

  // Get the appropriate email template
  let email = casesEmails[previousStep][type];

  // Map parameters for email and SMS
  const parameters = email?.parameters?.map((key) => {
    switch (key) {
      case 'caseId':
        return { key: 'CASE ID', value: activeCase.formattedId || activeCase.id };
      case 'lastName':
        return { key: 'PATIENT SURNAME', value: activeCase?.patientInfo?.lastName || '' };
      case 'date':
        return { key: 'DOS', value: activeCase.date ? moment(activeCase.date).format('DD/MM/YY') : 'TBC' };
      case 'surgeon':
        return { key: 'SURGEON', value: surgeonUser ? `${surgeonUser.firstName} ${surgeonUser.lastName}` : '' };
      case 'hospital':
        return { key: 'HOSPITAL', value: activeCase.hospital };
      case 'procedure':
        return { key: 'PROCEDURE', value: procedure ? procedure.name : '' };
      default:
        return { key: '', value: '' };
    }
  }) || [];

  // Prevent email sending for specific cases
  if (!activeCase.trainingMode) {
    if (
      [procedureFlows.ACL, procedureFlows.CMF, ...cmfExtendedProcedureFlows].includes(activeCase.procedureFlow) &&
      previousStep === 6
    ) {
      email = null;
    }

    // If there's an email template, send notifications
    if (email) {
      email.roles.forEach((role) => {
        const recipients = getRoleRecipients(role, surgeon, activeCase, users.list, teams.list, manufacturers.list, logistics.list);

        recipients.forEach((recipient) => {
          if (recipient && recipient.uid !== currentUser.uid && !recipient?.disabled) {
            dispatch(
              sendCaseEmail(
                recipient.email,
                email.subject({ ...activeCase, recipient }),
                email.message({ ...activeCase, recipient, caseId: activeCase.formattedId || activeCase.id }),
                email.additionalText({ ...activeCase, recipient }),
                true,
                parameters
              )
            );
          }
          if (recipient && recipient.phone && !recipient?.disabled) {
            const text = email.message({ ...activeCase, recipient, caseId: activeCase.formattedId || activeCase.id });
            api.post(urls.sendSms, { phone: recipient.phone, text, parameters });
          }
        });
      });
    }
  }

  dispatch(setNotification({ variant: 'success', vertical: 'bottom', message: 'Notifications were sent via SMS and email' }));
};

export const setActiveCase = (newCase) => (dispatch, getState) => {
  const { procedures } = getState();

  if (!newCase.procedureFlow) {
    const procedureFlow = procedures.list.find((pr) => pr.id === newCase.procedure)?.flow;
    newCase.procedureFlow = procedureFlow;
  }

  dispatch({ type: SET_ACTIVE_CASE, case: newCase });
};

export const setActiveCaseById = (caseId) => (dispatch) => {
  return dispatch({ type: SET_ACTIVE_CASE, case: { id: caseId } });
};

export const discardActiveCase = () => (dispatch) => {
  setTimeout(() => dispatch({ type: DISCARD_ACTIVE_CASE }), 500);
};

export const resetCases = () => ({ type: RESET_CASES });

export const deleteCase = (caseId) => async (dispatch, getState) => {
  try {
    const db = Firebase.db;
    const activeCase = getState().cases?.activeCase;

    await setDoc(doc(db, CASES_COLLECTION, caseId), { archived: true, isHydrated: deleteField() }, { merge: true });
    if (activeCase?.id === caseId) {
      dispatch({
        type: UPDATE_CASE,
        case: {
          ...activeCase,
          archived: true,
        },
      });
      updateCaseStatsOptimistically(dispatch, getState, (stats) => ({
        ...stats,
        totalCases: Math.max(0, stats.totalCases - (!activeCase?.archived && !activeCase?.postponed ? 1 : 0)),
        futureCases: Math.max(0, stats.futureCases - (isFutureCaseDate(activeCase?.date) && !activeCase?.archived && !activeCase?.postponed ? 1 : 0)),
        postponedCases: Math.max(0, stats.postponedCases - (!activeCase?.archived && !!activeCase?.postponed ? 1 : 0)),
      }));
    }
    scheduleCaseStatsRefresh(dispatch);
    dispatch(discardActiveCase());
    dispatch(
      setNotification({
        variant: 'success',
        vertical: 'top',
        message: 'Case successfully archived',
      })
    );
  } catch (error) {
    console.error('Error archiving case:', error);
  }
};

export const restoreCase = (caseId) => async (dispatch, getState) => {
  try {
    const db = Firebase.db;
    const activeCase = getState().cases?.activeCase;

    await setDoc(doc(db, CASES_COLLECTION, caseId), { archived: false, isHydrated: deleteField() }, { merge: true });
    if (activeCase?.id === caseId) {
      dispatch({
        type: UPDATE_CASE,
        case: {
          ...activeCase,
          archived: false,
        },
      });
      updateCaseStatsOptimistically(dispatch, getState, (stats) => ({
        ...stats,
        totalCases: stats.totalCases + (!activeCase?.postponed ? 1 : 0),
        futureCases: stats.futureCases + (!activeCase?.postponed && isFutureCaseDate(activeCase?.date) ? 1 : 0),
        postponedCases: stats.postponedCases + (activeCase?.postponed ? 1 : 0),
      }));
    }
    scheduleCaseStatsRefresh(dispatch);
    dispatch(discardActiveCase());
    dispatch(
      setNotification({
        variant: 'success',
        vertical: 'top',
        message: 'Case successfully restored',
      })
    );
  } catch (error) {
    console.error('Error restoring case:', error);
  }
};

export const setNotes = (caseId, notes) => ({ type: SET_CASE_NOTES, caseId, notes });

export const createNote = (activeCase, note) => async (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user.currentUser;
  const { uid } = currentUser;
  const users = state.users.list;
  const teams = state.teams.list;
  const surgeons = state.surgeons.list;
  const notesCollection = collection(Firebase.db, NOTES_SUB_COLLECTION(activeCase.id));

  const newNote = {
    ...note,
    external: canAccessInternalCaseNotes(currentUser?.role) ? !!note.external : true,
    userId: uid,
    authorName:
      currentUser.fullName ||
      [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' '),
    date: moment().format(),
  };

  try {
    // Add the note to Firestore
    const docRef = await addDoc(notesCollection, newNote);
    dispatch({ type: ADD_CASE_NOTE, caseId: activeCase.id, note: { id: docRef.id, ...newNote } });

    // Define recipients
    let recipients = [{ uid: 'casenote@personalisedsurgery.com', email: 'casenote@personalisedsurgery.com' }];
    const surgeonDoc = surgeons.find((s) => s.id === activeCase.surgeonId);
    const engineers = getEngineerRecipientsFromTeams(activeCase, users, teams);
    const salesReps = Array.isArray(surgeonDoc?.salesRep) ? surgeonDoc.salesRep : [surgeonDoc?.salesRep];

    engineers.forEach((user) => {
      if (user && user.email && !recipients.some((r) => r.email === user.email)) {
        recipients.push(user);
      }
    });

    if (newNote.external) {
      salesReps.forEach((salesRepUid) => {
        const user = users.find((u) => u.uid === salesRepUid);
        if (user && user.email && !recipients.some((r) => r.email === user.email)) {
          recipients.push(user);
        }
      });

      const surgeonUser = users.find((user) => user.uid === (activeCase.surgeon || state.cases.activeCase.surgeon).userId);
      if (surgeonUser) {
        recipients.push(surgeonUser);
      }
    }

    // Filter out the current user from the recipients
    recipients = recipients.filter((user) => (
      user.uid !== uid &&
      (user.uid === 'casenote@personalisedsurgery.com' || userMatchesCaseCompany(user, activeCase, state))
    ));

    // Send notifications and emails to recipients
    await Promise.all(
      recipients.map((recipient) => {
        if (!recipient?.disabled) {
          return dispatch(
            sendCaseEmail(
              recipient.email,
              'New Case Note',
              `New note was added for the case ${activeCase?.formattedId || activeCase?.id}`,
              newNote.text
            )
          );
        }

        return null;
      })
    );

    await Promise.all(
      recipients.map((recipient) => {
        if (!recipient?.disabled) {
          return dispatch(
            createActivity(recipient.uid, withCaseActivityCompany(activeCase, {
              type: 'notification',
              title: 'New Case Note',
              text: newNote.text,
              caseId: activeCase.id,
              date: moment().format(),
              userId: uid || '',
            }, state))
          );
        }

        return null;
      })
    );
  } catch (error) {
    console.error('Error creating note:', error);
  }
};

export const updateCaseNote = (activeCase, note, text) => async (dispatch, getState) => {
  const state = getState();
  const currentUserId = state.user.currentUser?.uid;
  const nextText = text?.trim();

  if (!activeCase?.id || !note?.id || !currentUserId || note.userId !== currentUserId || !nextText?.length) {
    return false;
  }

  const editedAt = moment().format();

  try {
    if (note.source === 'case') {
      await updateDoc(doc(Firebase.db, CASES_COLLECTION, activeCase.id), {
        caseNote: nextText,
        caseNoteEditedAt: editedAt,
        isHydrated: deleteField(),
      });

      dispatch({
        type: SET_ACTIVE_CASE,
        case: {
          ...state.cases.activeCase,
          caseNote: nextText,
          caseNoteEditedAt: editedAt,
        },
      });

      return true;
    }

    await updateDoc(doc(Firebase.db, NOTES_SUB_COLLECTION(activeCase.id), note.id), {
      text: nextText,
      editedAt,
    });

    dispatch({
      type: UPDATE_CASE_NOTE,
      note: {
        ...note,
        text: nextText,
        editedAt,
      },
    });

    return true;
  } catch (error) {
    console.error('Error updating note:', error);
    return false;
  }
};

export const fetchNotes = (caseId) => async (dispatch, getState) => {
  const notesCollection = collection(Firebase.db, NOTES_SUB_COLLECTION(caseId));
  const role = getState().user.currentUser?.role;

  try {
    dispatch({ type: LOAD_CASE_NOTES, caseId });

    const snapshot = await getDocs(
      canAccessInternalCaseNotes(role) ? notesCollection : query(notesCollection, where('external', '==', true))
    );
    const notes = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

    dispatch(setNotes(caseId, orderBy(notes, 'date')));
  } catch (error) {
    dispatch(setNotes(caseId, []));
    console.error('Error fetching notes:', error);
  }
};

export const sendCaseEmail = (recipient, subject, message, additionalText, isCase, parameters) => async (dispatch, getState) => {
  const { notifications } = getState().settings;

  if (notifications?.active) {
    try {
      return sendEmail(recipient, subject, message, additionalText, isCase, parameters);
    } catch (error) {
      console.error('Error sending email:', error);
    }
  }
};

const fetchSurgeonData = async (surgeonRef) => {
  const surgeonDoc = await getDoc(surgeonRef);
  return surgeonDoc.exists() ? { id: surgeonDoc.id, ...surgeonDoc.data() } : null;
};

export const subscribeToCases = () => (dispatch, getState) => {
  const db = Firebase.db;
  const state = getState();
  const currentUser = state.user.currentUser;
  const uid = currentUser?.uid;
  const role = currentUser?.role;
  const users = state.users.list;
  const distributors = state.distributors.list;

  // Skip subscription if user role is Radiology
  if (role === userRoles.RADIOLOGY.name) {
    console.log("Subscription skipped for Radiology role.");
    return () => {}; // Return a no-op function as the unsubscribe placeholder
  }

  let unsubscribe;

  // Fetch surgeons and set up subscription
  fetchSurgeons(uid, role).then((surgeons) => {
    const surgeonIds = surgeons.map((surgeon) => surgeon.id);

    if (!surgeonIds.length && role !== userRoles.GLOBAL_ADMIN.name) {
      console.log("No assigned surgeons, skipping subscription.");
      dispatch(setCases([]));
      return;
    }

    // Firestore query for all cases
    const casesQuery = query(
      collection(db, CASES_COLLECTION),
      where('status', '!=', 'COMPLETED'),
      // where('archived', '!=', true),
    );
    // const casesQuery = query(collection(db, CASES_COLLECTION));

    // Subscribe to the query
    unsubscribe = onSnapshot(
      casesQuery,
      async (snapshot) => {
        const cases = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        console.log(`Fetched ${cases.length} cases`);

        // Fetch surgeon data and enrich cases
        const enrichedCases = await Promise.all(
          cases.map(async (caseData) => {
            const surgeonRef =
              typeof caseData.surgeon === "string"
                ? doc(Firebase.db, caseData.surgeon)
                : caseData.surgeon;
            const surgeonData = await fetchSurgeonData(surgeonRef);

            const surgeonUser = users.find(
              (user) => user.uid === surgeonData?.userId
            );
            const surgeonName = surgeonUser
              ? `${surgeonUser?.firstName} ${surgeonUser?.lastName}`
              : "";
            const currentUser = state.user.currentUser;
            const isTrainingMode = caseData.trainingMode;

            return {
              ...caseData,
              surgeon: surgeonData,
              surgeonName,
              access: hasAccess(
                currentUser,
                surgeonData,
                caseData.step,
                caseData.procedureFlow,
                isTrainingMode
              ),
            };
          })
        );

        // Filter cases by assigned surgeons if role is not GLOBAL_ADMIN
        let filteredCases = enrichedCases;
        if (role !== userRoles.GLOBAL_ADMIN.name) {
          filteredCases = enrichedCases.filter((caseItem) =>
            surgeonIds.includes(caseItem.surgeon?.id)
          );
        }

        // Apply role-specific filtering
        if (role === userRoles.COUNTRY_ADMIN.name) {
          filteredCases = filteredCases.filter((item) =>
            isCountryAdminForCase(currentUser, {
              ...item,
              country: item.country || distributors.find((d) => d.id === item.distributorId)?.country,
            }, state)
          );
        }

        filteredCases = filteredCases.filter((caseItem) => {
          if (role === userRoles.SURGEON.name) {
            return caseItem.surgeon?.userId === uid;
          }
          return true;
        });

        // Dispatch sorted cases
        dispatch(
          setCases(filteredCases.sort((a, b) => (a.date > b.date ? 1 : -1)))
        );
      },
      (error) => {
        console.error("Error fetching cases:", error);
      }
    );
  });

  // Return the unsubscribe function
  return () => {
    if (unsubscribe) unsubscribe();
  };
};

// Filter on BE using chunks:
// export const subscribeToCases = () => (dispatch, getState) => {
//   const db = Firebase.db;
//   const state = getState();
//   const uid = state.user.currentUser?.uid;
//   const role = state.user.currentUser?.role;
//   const country = state.user?.currentUser?.administrationCountry || null;
//   const users = state.users.list;
//   const distributors = state.distributors.list;
//
//   // Skip subscription if user role is Radiology
//   if (role === userRoles.RADIOLOGY.name) {
//     console.log("Subscription skipped for Radiology role.");
//     return () => {}; // Return a no-op function as the unsubscribe placeholder
//   }
//
//   let unsubscribes = []; // Store multiple unsubscribe functions
//   let casesFetchedCount = 0; // Track the number of queries completed
//   const allCases = new Map(); // To merge and deduplicate cases
//
//   // Fetch surgeons and set up subscription
//   fetchSurgeons(uid, role).then((surgeons) => {
//     const surgeonIds = surgeons.map((surgeon) => surgeon.id);
//
//     if (!surgeonIds.length && role !== userRoles.GLOBAL_ADMIN.name) {
//       console.log("No assigned surgeons, skipping subscription.");
//       dispatch(setCases([]));
//       return;
//     }
//
//     // Firestore queries
//     const casesQueries =
//       role === userRoles.GLOBAL_ADMIN.name
//         ? [query(collection(db, CASES_COLLECTION))] // One query for GLOBAL_ADMIN
//         : chunk(surgeonIds, 10).map((idsChunk) =>
//           query(
//             collection(db, CASES_COLLECTION),
//             where("surgeonId", "in", idsChunk) // Split queries into chunks of 10 surgeon IDs
//           )
//         );
//
//     // Subscribe to each query and merge results
//     unsubscribes = casesQueries.map((casesQuery, index) =>
//       onSnapshot(
//         casesQuery,
//         async (snapshot) => {
//           const cases = snapshot.docs.map((doc) => ({
//             id: doc.id,
//             ...doc.data(),
//           }));
//
//           console.log(`Fetched ${cases.length} cases from query ${index}:`, cases);
//
//           // Fetch surgeon data and enrich cases
//           const enrichedCases = await Promise.all(
//             cases.map(async (caseData) => {
//               const surgeonRef =
//                 typeof caseData.surgeon === "string"
//                   ? doc(Firebase.db, caseData.surgeon)
//                   : caseData.surgeon;
//               const surgeonData = await fetchSurgeonData(surgeonRef);
//
//               const surgeonUser = users.find(
//                 (user) => user.uid === surgeonData?.userId
//               );
//               const surgeonName = surgeonUser
//                 ? `${surgeonUser?.firstName} ${surgeonUser?.lastName}`
//                 : "";
//               const currentUser = state.user.currentUser;
//               const isTrainingMode = caseData.trainingMode;
//
//               return {
//                 ...caseData,
//                 surgeon: surgeonData,
//                 surgeonName,
//                 access: hasAccess(
//                   currentUser,
//                   surgeonData,
//                   caseData.step,
//                   caseData.procedureFlow,
//                   isTrainingMode
//                 ),
//               };
//             })
//           );
//
//           // Update allCases map
//           enrichedCases.forEach((caseItem) => {
//             allCases.set(caseItem.id, caseItem); // Deduplicate by case ID
//           });
//
//           casesFetchedCount += 1;
//
//           // Check if all queries are complete
//           if (casesFetchedCount === casesQueries.length) {
//             let finalCases = Array.from(allCases.values());
//             if (role === userRoles.COUNTRY_ADMIN.name) {
//               finalCases = finalCases.filter((item) =>
//                 distributors
//                   .find((d) => d.id === item.distributorId)
//                   ?.country === country
//               );
//             }
//
//             const filteredCasesByRole = finalCases.filter((caseItem) => {
//               if (role === userRoles.SURGEON.name) {
//                 return caseItem.surgeon?.userId === uid && !caseItem?.archived;
//               }
//               return !caseItem?.archived;
//             });
//
//             // Dispatch sorted cases only once
//             dispatch(
//               setCases(filteredCasesByRole.sort((a, b) => (a.date > b.date ? 1 : -1)))
//             );
//           }
//         },
//         (error) => {
//           console.error("Error fetching cases:", error);
//         }
//       )
//     );
//   });
//
//   // Return a combined unsubscribe function
//   return () => {
//     unsubscribes.forEach((unsubscribe) => unsubscribe && unsubscribe());
//   };
// };
